import React from 'react';
import moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip';
import DropdownButton from 'react-bootstrap/lib/DropdownButton';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import { sortBy } from 'lodash/collection';
import { Tab, Tabs } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ReactComponent as CancelIcon } from '../sf-components/Icons/Cancel.svg';

import SystemStatusTabs, { SYSTEM_STATUS_TABS } from './SystemStatusTabs/SystemStatusTabs';
import NodeStatusRow from './NodeStatusRow';
import DCStatusRow from './DCStatusRow';
import PushServiceStatusRow from './PushServiceStatusRow';
import {
    httpGet,
    httpPost,
    isPasswordValid,
    isUserPermitted,
    removeTrailingTags,
    updateUserToState,
    uppercaseFirstLetter
} from '../utils';
import ConfirmationMessage from '../Controls/ConfirmationMessage';
import {
    AD_CONNECT_STATUSES,
    DCS_STATES,
    FEATURE_FLAGS_NAMES,
    HTTP_OPTIONS,
    LAYOUT_TABS,
    NODES_STATES,
    PUSH_SERVICES_STATUS,
    ROLES,
    SYSTEM_STATUS_ACTIONS,
    SYSTEM_STATUS_INTERVAL,
    SYSTEM_STATUS_TABS_TYPES
} from '@silverfort-nodelibs/common/utils/consts';
import * as filterTypes from '../Controls/Filters/filterTypes';

import activeStateIcon from './Images/active_icon.png';
import nonactiveStateIcon from './Images/nonactive_icon.png';
import powerIcon from './Images/power.png';
import loadingIcon from '../common/NewImages/loading.svg';
import vCircleIcon from '../common/NewImages/v-circle.svg';
import cloudUpIcon from '../common/NewImages/cloud-up.svg';
import fileIcon from '../common/NewImages/file.svg';
import activeSpsIcon from './Images/active_sps.png';
import nonactiveSpsIcon from './Images/non_active_sps.png';

import adSyncSuccessIcon from './Images/ad_sync_success.svg';
import adSyncFailedIcon from './Images/ad_sync_failed.svg';
import { SYSTEM_MODE_HIDDEN_VIEWS, SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import AdConnectStatusRow from './AdConnectStatusRow';
import Snackbar from '../Controls/Snackbar';
import SnackbarSelectActions from '../Controls/SnackbarSelectActions';
import AdminConsoleStatusRow from './AdminConsoleStatusRow';
import DropdownMenu from '../DropdownMenu/DropdownMenu';
import DropdownMenuItem from '../DropdownMenu/MenuItem';
import LogManager from './LogManager';
import Icons from '../sf-components/Icon/Icons';
import Button from '../sf-components/Button/Button';
import Icon from '../sf-components/Icon/Icon';
import ImportBackup from '../SystemSettings/components/SystemBackup/importBackup';
import CreateBackup from '../SystemSettings/components/SystemBackup/createBackup';
import BackupHistory from './SystemStatusTabs/BackupsHistory';
import SchedulerBackup from '../SystemSettings/components/SystemBackup/SchedulerBackup';
import useFeature from '../SystemSettings/hooks/featureFlags/useFeature';
import { isFeatureActive } from '@silverfort-nodelibs/common/utils/common';
import { MmsCmsConnectivityStatus } from './SystemSettingsComponents/MmsCmsConnectivityStatus';
import { PricingModules, usePricingModulesState } from '../../context/contexts/PricingModuleContext';
import { isAnyEnablingModulesActive } from '../SystemSettings/hooks/lockedFeatures/utils';
import { DisabledDisplayOptions, RequiredModuleState } from '../SystemSettings/hooks/lockedFeatures/types';
import { FeatureLock } from '../common/LockedFeatures';
import { Icon as LibIcon, Icons as LibIcons } from '@silverfort/sf-components';
import { isSystemTabHiddenBySystemMode } from '../SystemSettings/common/utils';
import { useIsCloudOnly } from '../SystemSettings/hooks/featureFlags/useIsCloudOnly';

const NODES_STATES_FOR_FILTER = { ...NODES_STATES };
delete NODES_STATES_FOR_FILTER['STARTING'];
delete NODES_STATES_FOR_FILTER['STOPPING'];
const LOGS_ACTIONS = {
    GENERATING: 'generating',
    GENERATING_ENDED: 'generatingEnded',
    SENDING: 'sending',
    SENDING_ENDED: 'sendingEnded',
    SELECTING: 'selecting'
};

const ENDED_LOG_ACTION_TIME_OUT = 5000;

const isServiceBusEnabled = (settingsData) => {
    return settingsData?.RavenSendConnectionStrings?.length > 0;
};

const StyledTabs = ({ domainsData }) => {
    const tabsClasses = makeStyles({
        indicator: {
            display: 'none'
        }
    })();
    const tabClasses = makeStyles({
        root: {
            textTransform: 'none',
            letterSpacing: 'normal',
            fontFamily: 'Saira',
            padding: '0 ',
            opacity: 1,
            borderRight: '1px solid rgba(207, 210, 212, 0.7)',
            cursor: 'default'
        },
        wrapper: {
            lineHeight: '1.4'
        }
    })();

    return (
        <Tabs
            value={0}
            variant="scrollable"
            scrollButtons="auto"
            classes={{ indicator: tabsClasses.indicator }}
            onClick={(e) => e.stopPropagation()}>
            {domainsData.map((domain, index) => (
                <Tab
                    disableRipple
                    key={`DomainData_${index}`}
                    value={index}
                    label={domain}
                    wrapped
                    classes={{
                        root: tabClasses.root,
                        selected: tabClasses.selected
                    }}
                />
            ))}
        </Tabs>
    );
};

const backupPasswordInfo = (
    <div>
        Your password must meet the following requirements:
        <ul className="list-disc m-0">
            <li className="ml-4">Be at least 8 characters long</li>
            <li className="ml-4">
                Include at least one uppercase letter, one lowercase letter, one number, and one special character
            </li>
        </ul>
        Remember to save your password securely; Silverfort cannot recover it.
    </div>
);

const getBackupPasswordError = (password) => {
    return isPasswordValid(password) ? (
        ''
    ) : (
        <>
            Invalid password:
            <ul className="list-disc m-0">
                <li className="ml-4">Must include at least 8 characters</li>
                <li className="ml-4">Must include at least one uppercase letter</li>
                <li className="ml-4">Must include at least one lowercase letter</li>
                <li className="ml-4">Must include at least one number</li>
                <li className="ml-4">Must include at least one special character</li>
            </ul>
        </>
    );
};

class SystemStatus extends React.Component {
    state = {
        data: [],
        IsLoading: true,
        Walls: [],
        OnRefresh: {},
        RefreshData: true,
        StateChangeTicks: 0,
        IsDownloadLogError: false,
        IsSendingLog: false,
        IsSendingLogError: false,
        SystemState: true,
        showSendLogsMenu: false,
        showDownloadExtendedDiagnosticsMenu: false,
        showGenerateLogsMenu: false,
        showDownloadLogsMenu: false,
        selectedTab:
            SYSTEM_STATUS_TABS[
                SYSTEM_MODE_HIDDEN_VIEWS[LAYOUT_TABS.SYSTEM_STATUS.ID][this.props.systemMode].selectedTab
            ],
        ADSyncGenerated: false,
        ShutdownGenerated: false,
        ShutdownError: false,
        PowerupGenerated: false,
        PowerupError: false,
        DesiredStates: {},
        CompanyLogo: 'company_logo.png',
        LicenseExpiresInHours: null,
        LicenseTrialMode: false,
        IsLogServerConnected: false,
        checkingLogServerConnection: true,
        isOffline: false,
        showErrorDot: false,
        showPushServicesErrorDot: false,
        showNodesWarningDot: false,
        skipTimerUpdate: false,
        filters: {
            [SYSTEM_STATUS_TABS.NODES]: {
                nodeName: {
                    id: 'nodeName',
                    label: 'Node name',
                    filterType: filterTypes.SELECT,
                    filterConfig: {
                        options: [],
                        searchPlaceholder: 'Search node names...',
                        allowSearch: true,
                        searchValue: ''
                    },
                    hasClearWhenEmpty: false,
                    value: [],
                    defaultValue: []
                },
                connectedDcs: {
                    id: 'connectedDcs',
                    label: 'Connected DCs',
                    filterType: filterTypes.SELECT,
                    filterConfig: {
                        options: [],
                        searchPlaceholder: 'Search connected DCs...',
                        allowSearch: true,
                        searchValue: ''
                    },
                    hasClearWhenEmpty: false,
                    value: [],
                    defaultValue: []
                },
                nodeStatus: {
                    id: 'nodeStatus',
                    label: 'Node status',
                    filterType: filterTypes.SELECT,
                    filterConfig: {
                        options: Object.keys(NODES_STATES_FOR_FILTER).map((nodeStatus) => {
                            return { value: NODES_STATES[nodeStatus].ID, label: NODES_STATES[nodeStatus].DISPLAY };
                        })
                    },
                    value: Object.values(NODES_STATES_FOR_FILTER).map((nodeState) => {
                        return nodeState.ID;
                    }),
                    defaultValue: Object.values(NODES_STATES_FOR_FILTER).map((nodeState) => {
                        return nodeState.ID;
                    })
                },
                domainName: {
                    id: 'domainName',
                    label: 'Domain name',
                    filterType: filterTypes.SELECT,
                    filterConfig: {
                        options: [],
                        searchPlaceholder: 'Search domain name...',
                        allowSearch: true,
                        searchValue: ''
                    },
                    hasClearWhenEmpty: false,
                    value: [],
                    defaultValue: []
                }
            },
            [SYSTEM_STATUS_TABS.DCS]: {
                dcName: {
                    id: 'dcName',
                    label: 'DC name',
                    filterType: filterTypes.SELECT,
                    filterConfig: {
                        options: [],
                        searchPlaceholder: 'Search DC name...',
                        allowSearch: true,
                        searchValue: ''
                    },
                    hasClearWhenEmpty: false,
                    value: [],
                    defaultValue: []
                },
                activeNodes: {
                    id: 'activeNodes',
                    label: 'Active nodes',
                    filterType: filterTypes.SELECT,
                    filterConfig: {
                        options: [],
                        searchPlaceholder: 'Search active nodes...',
                        allowSearch: true,
                        searchValue: ''
                    },
                    hasClearWhenEmpty: false,
                    value: [],
                    defaultValue: []
                },
                domainName: {
                    id: 'domainName',
                    label: 'Domain name',
                    filterType: filterTypes.SELECT,
                    filterConfig: {
                        options: [],
                        searchPlaceholder: 'Search domain names...',
                        allowSearch: true,
                        searchValue: ''
                    },
                    hasClearWhenEmpty: false,
                    value: [],
                    defaultValue: []
                },
                recoverStatus: {
                    id: 'recoverStatus',
                    label: 'Recoverable Dcs',
                    filterType: filterTypes.FLAT_RADIO,
                    filterConfig: {
                        options: [
                            { value: 'all', label: 'All DCs' },
                            { value: 'recoverableDcs', label: 'Recoverable DCs' }
                        ]
                    },
                    value: 'all',
                    defaultValue: 'all'
                },
                isProtected: {
                    id: 'isProtected',
                    label: 'Protected Dcs',
                    filterType: filterTypes.FLAT_RADIO,
                    filterConfig: {
                        options: [
                            { value: 'all', label: 'All DCs' },
                            { value: 'protectedDcs', label: 'Protected' },
                            { value: 'notProtectedDcs', label: 'Unprotected' }
                        ]
                    },
                    value: 'all',
                    defaultValue: 'all'
                }
            },
            [SYSTEM_STATUS_TABS.PUSH_SERVICES]: {
                pushServiceName: {
                    id: 'pushServiceName',
                    label: 'Messaging service name',
                    filterType: filterTypes.SELECT,
                    filterConfig: {
                        options: [],
                        searchPlaceholder: 'Search DC name...',
                        allowSearch: true,
                        searchValue: ''
                    },
                    hasClearWhenEmpty: false,
                    value: [],
                    defaultValue: []
                }
            },
            [SYSTEM_STATUS_TABS.AD_CONNECT]: {
                adConnectName: {
                    id: 'adConnectName',
                    label: 'Ad connect host name',
                    filterType: filterTypes.SELECT,
                    filterConfig: {
                        options: [],
                        searchPlaceholder: 'Ad connect host name...',
                        allowSearch: true,
                        searchValue: ''
                    },
                    hasClearWhenEmpty: false,
                    value: [],
                    defaultValue: []
                }
            }
        },
        SettingsData: {},
        logsActions: [],
        Nodes: [],
        filterNodesIds: [],
        nodeRows: [],
        selectedNodes: new Set(),
        DomainControllers: [],
        filterDcsIds: [],
        dcsRows: [],
        selectedDcs: new Set(),
        PushServices: [],
        filterPushServicesIds: [],
        pushServicesRows: [],
        selectedPushServices: new Set(),
        adConnectDisplayData: [],
        filterAdConnectsIds: [],
        adConnectRows: [],
        adConnectsRows: [],
        selectedAdConnects: new Set(),
        adminConsoleRows: [],
        isAdminConsoleSelected: false,
        activeNodes: 0,
        activeDcs: 0,
        activePushServices: 0,
        activeAdConnecters: 0,
        confirmationMessage: {
            showConfirmation: false,
            title: undefined,
            body: undefined,
            approveText: undefined,
            approveStyle: undefined,
            onCancel: () => {},
            onApprove: () => {}
        },
        allDcsDuringRecover: false,
        isEditor: false,
        canPreformAdsync: false,
        AutomaticADSyncEnabled: false,
        isGeneratingAdminConsoleLogs: false,
        isGeneratingAdminConsoleError: false,
        isDownloadingAdminConsoleError: false,
        sendAllLogsLastSendingDate: null,
        isTowerLogExists: false,
        isPlatformLogExists: false,
        IsPlatformLogGenerating: false,
        lastTowerLogGenerated: null,
        isGeneratingAllLogs: false,
        isGeneratingAllError: false,
        rebalanceAzDisabledNodes: [],
        dcCaptureConfig: {},
        azConfig: {},
        logsToSend: [],
        logsToSendInProgress: [],
        logsToGenerate: [],
        logsToGenerateInProgress: [],
        showSendLogFinished: false,
        logGeneratedNumber: 0,
        logSentNumber: 0,
        deleteLogsToDelayTime: {}
    };

    getChangeStatusModalDetails(NewStateUp) {
        return {
            showConfirmation: true,
            title: NewStateUp ? 'Start Silverfort' : 'Disable Silverfort',
            body: `Are you sure you want to ${NewStateUp ? 'start' : 'disable'} Silverfort?`,
            checkboxText: `${NewStateUp ? 'Enable' : 'Disable'} Automatic AD Sync`,
            checkboxExtraData: (
                <span>
                    This will disable automatic AD Sync on Nodes. AD Sync will continue to run until it completes.
                    <br />
                    After enabling the Silverfort, it will run a Full AD Sync on all Nodes.
                    {this.props.pasFeatureEnabled && (
                        <>
                            <br />
                            {`This action will also ${
                                NewStateUp ? 're-disable' : 're-enable'
                            } all privileged accounts disabled by a JIT policy. It will
                            take 2 minutes to complete.`}
                        </>
                    )}
                </span>
            ),
            checkBoxInitialState: true,
            approveText: NewStateUp ? 'Start Silverfort' : 'Disable Silverfort',
            approveStyle: NewStateUp ? 'primary' : 'danger',
            onCancel: this.closeConfirmation,
            onApprove: this.ChangeSilverfortStatus
        };
    }

    getDownloadLogsModalDetails(downloadFunc) {
        return {
            showConfirmation: true,
            title: 'Download log',
            body: `The last log was generated more than ${this.state.logsWarningDaysDiff} day${
                this.state.logsWarningDaysDiff > 1 ? 's' : ''
            } ago. Are you sure you want to download?`,
            approveText: 'Download Log',
            onCancel: this.closeConfirmation,
            onApprove: () => {
                this.closeConfirmation();
                downloadFunc();
            }
        };
    }

    getAutomaticADSyncModalDetails() {
        return {
            showConfirmation: true,
            title: this.state.AutomaticADSyncEnabled ? 'Disable automatic AD Sync' : 'Enable automatic AD Sync',
            body: (
                <div>
                    {`Are you sure you want to 
                        ${this.state.AutomaticADSyncEnabled ? 'disable' : 'enable'}
                         automatic AD Sync?`}
                    {this.state.AutomaticADSyncEnabled && (
                        <span>Warning: This will disable automatic AD Sync on all Silverforts Nodes.</span>
                    )}
                </div>
            ),
            approveText: this.state.AutomaticADSyncEnabled ? 'Disable' : 'Enable',
            approveStyle: this.state.AutomaticADSyncEnabled ? 'danger' : 'primary',
            onCancel: this.closeConfirmation,
            onApprove: (cb) => {
                this.toggleAutoADSyncState(cb);
            }
        };
    }

    async toggleAutoADSyncState(cb) {
        try {
            const newAutoADSyncState = !this.state.AutomaticADSyncEnabled;
            await httpPost('/Settings/changeAutoADSyncState', { newState: newAutoADSyncState });
            this.setState({
                AutomaticADSyncEnabled: newAutoADSyncState,
                confirmationMessage: { ...this.state.confirmationMessage, showConfirmation: false }
            });
        } catch (err) {
            cb(false, 'Error Occurred');
            console.error(err);
        }
    }

    async componentDidMount() {
        const locale = navigator.languages ? navigator.languages : navigator.language;

        moment.locale(locale);
        const startTime = new Date();

        await updateUserToState(this);
        httpGet(`/Settings/checkConnectionToLogServer`)
            .then((response) => {
                this.setState({
                    IsLogServerConnected: response.data.isConnected,
                    isOffline: response.data.isOffline
                });
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => this.setState({ checkingLogServerConnection: false }));

        httpGet(`/Settings/GetSystemData`)
            .then((response) => {
                const systemData = response.data;

                let towerSyncDate = null;
                if (systemData.LastTowerSync) {
                    const updateTime = moment.utc(systemData.LastTowerSync).local();
                    towerSyncDate = `${updateTime.format('L')}, ${updateTime.format('LTS')}`;
                }
                const isEditor = isUserPermitted(this.state.user, 'systemStatus', ROLES.EDITOR.value);
                const canPreformAdsync = isUserPermitted(this.state.user, 'preformAdsync', ROLES.EDITOR.value);
                const towerVersion =
                    this.props.systemMode === SYSTEM_MODES.SAAS ? '' : removeTrailingTags(systemData.TowerVersion);

                this.setState(
                    {
                        SettingsData: systemData.Settings,
                        CompanyName: systemData.CompanyName,
                        CompanyLogo: systemData.CompanyLogo,
                        TowerSync: towerSyncDate,
                        TowerVersion: towerVersion,
                        Nodes: systemData.Nodes,
                        DomainControllers: systemData.DomainControllers,
                        PushServices: systemData.PushServices,
                        adConnectDisplayData: systemData.adConnectDisplayData,
                        SystemState: systemData.SystemState,
                        SystemStateInProgress: systemData.SystemStateInProgress,
                        RefreshData: true,
                        isRavenConnected: systemData.isRavenConnected,
                        isPhoenixConnected: systemData.isPhoenixConnected,
                        showCMSBanner: !systemData.isPhoenixConnected && this.props.systemMode !== SYSTEM_MODES.SAAS,
                        SendQueueStats: systemData.SendQueueStats,
                        IsSendingLog: systemData.SendQueueStats.length > 0,
                        LicenseExpiresInHours: systemData.LicenseExpiresInHours,
                        LicenseTrialMode: systemData.LicenseTrialMode,
                        isAryaConnected: systemData.isAryaConnected,
                        isAryaEnabled: systemData.isAryaEnabled,
                        isDuringPolicyVersionChange: systemData.isDuringPolicyVersionChange,
                        logsWarningDaysDiff: systemData.logsWarningDaysDiff,
                        AutomaticADSyncEnabled: systemData.AutomaticADSyncEnabled,
                        isEditor: isEditor,
                        canPreformAdsync: canPreformAdsync,
                        isTowerLogExists: systemData.IsTowerLogExists,
                        sendAllLogsLastSendingDate: systemData.sendAllLogsLastSendingDate,
                        isPlatformLogExists: systemData.IsPlatformLogExists,
                        isPlatformLogGenerating: systemData.IsPlatformLogGenerating,
                        isGeneratingAdminConsoleLogs: systemData.IsTowerLogGenerating,
                        lastTowerLogGenerated: systemData.LastTowerLogGenerated,
                        lastTowerLogSent: systemData.LastTowerLogSent,
                        dcCaptureConfig: systemData.dcCaptureConfig,
                        azConfig: systemData.azConfig,
                        logsToSendInProgress: systemData.logsToSendInProgress,
                        isNewLogsGenerated: this.state.logsToGenerateInProgress.some(
                            (log) => !systemData.logsToGenerateInProgress.find((newLog) => newLog === log)
                        )
                            ? true
                            : this.state.isNewLogsGenerated,
                        isNewLogsSent: this.state.logsToSendInProgress.some(
                            (log) => !systemData.logsToSendInProgress.find((newLog) => newLog === log)
                        )
                            ? true
                            : this.state.isNewLogsSent,
                        logsToGenerateInProgress: systemData.logsToGenerateInProgress,
                        deleteLogsToDelayTime: systemData.deleteLogsToDelayTime,
                        dbStatisticsEnabled: systemData.dbStatisticsEnabled
                    },
                    () => {
                        this.updateNodeRows();
                        this.updateDcsRows();
                        this.updatePushServicesRows();
                        this.updateAdConnectRows();
                        this.updateAdminConsoleRows();
                        this.updateShowErrorDot();
                        this.updateFiltersOptions();
                        this.isSendingRequestInProgress();
                        this.isGeneratingRequestInProgress();
                    }
                );

                const endTime = new Date();
                const diff = endTime.getTime() - startTime.getTime();
                if (diff < 1000) {
                    setTimeout(() => {
                        this.setState({ IsLoading: false });
                    }, 1000 - diff);
                } else {
                    this.setState({ IsLoading: false });
                }

                setTimeout(() => {
                    this.RefreshSystemStatus();
                }, SYSTEM_STATUS_INTERVAL);
            })
            .catch((err) => {
                console.error(err);
            });
    }

    async RefreshSystemStatus() {
        try {
            const response = await httpGet(`/Settings/GetSystemData`);

            const systemData = response.data;
            let towerSyncDate = null;
            if (systemData.LastTowerSync) {
                const updateTime = moment.utc(systemData.LastTowerSync).local();
                towerSyncDate = `${updateTime.format('L')}, ${updateTime.format('LTS')}`;
            }

            if (!this.state.skipTimerUpdate) {
                this.setState(
                    {
                        SettingsData: systemData.Settings,
                        TowerSync: towerSyncDate,
                        Nodes: systemData.Nodes,
                        DomainControllers: systemData.DomainControllers,
                        PushServices: systemData.PushServices,
                        adConnectDisplayData: systemData.adConnectDisplayData,
                        SystemState: systemData.SystemState,
                        SystemStateInProgress: systemData.SystemStateInProgress,
                        SendQueueStats: systemData.SendQueueStats,
                        IsSendingLog: systemData.SendQueueStats.length > 0,
                        isRavenConnected: systemData.isRavenConnected,
                        isPhoenixConnected: systemData.isPhoenixConnected,
                        LicenseExpiresInHours: systemData.LicenseExpiresInHours,
                        LicenseTrialMode: systemData.LicenseTrialMode,
                        isDuringPolicyVersionChange: systemData.isDuringPolicyVersionChange,
                        sendAllLogsLastSendingDate: systemData.sendAllLogsLastSendingDate,
                        isTowerLogExists: systemData.IsTowerLogExists,
                        isPlatformLogExists: systemData.IsPlatformLogExists,
                        isGeneratingAdminConsoleLogs: systemData.IsTowerLogGenerating,
                        lastTowerLogGenerated: systemData.LastTowerLogGenerated,
                        lastTowerLogSent: systemData.LastTowerLogSent,
                        dcCaptureConfig: systemData.dcCaptureConfig,
                        azConfig: systemData.azConfig,
                        logsToSendInProgress: systemData.logsToSendInProgress,
                        isNewLogsGenerated: this.state.logsToGenerateInProgress.some(
                            (log) => !systemData.logsToGenerateInProgress.find((newLog) => newLog === log)
                        )
                            ? true
                            : this.state.isNewLogsGenerated,
                        isNewLogsSent: this.state.logsToSendInProgress.some(
                            (log) => !systemData.logsToSendInProgress.find((newLog) => newLog === log)
                        )
                            ? true
                            : this.state.isNewLogsSent,
                        logsToGenerateInProgress: systemData.logsToGenerateInProgress,
                        deleteLogsToDelayTime: systemData.deleteLogsToDelayTime
                    },
                    () => {
                        this.updateNodeRows();
                        this.updateDcsRows();
                        this.updatePushServicesRows();
                        this.updateAdConnectRows();
                        this.updateAdminConsoleRows();
                        this.updateShowErrorDot();
                        this.updateFiltersOptions();
                        this.isSendingRequestInProgress();
                        this.isGeneratingRequestInProgress();
                    }
                );
            }

            setTimeout(() => {
                this.RefreshSystemStatus();
            }, SYSTEM_STATUS_INTERVAL);
        } catch (err) {
            console.error(err);
            setTimeout(() => {
                this.RefreshSystemStatus();
            }, SYSTEM_STATUS_INTERVAL);
        } finally {
            if (this.state.skipTimerUpdate) {
                this.setState({ skipTimerUpdate: false });
            }
        }
    }

    componentWillUnmount() {
        this.setState({ RefreshData: false });
    }

    RefreshStatus() {
        if (this.state.RefreshData && this.state.OnRefresh) {
            this.state.OnRefresh();
        }

        setTimeout(() => {
            this.RefreshStatus();
        }, 5000);
    }

    SendActiveDirectorySyncRequest = async (isFullAdSync) => {
        this.setState({ ADSyncGenerated: true });

        let adSyncEndPoint = '/Settings/SyncActiveDirectory';
        if (this.props.systemMode === SYSTEM_MODES.SAAS || this.props.systemMode === SYSTEM_MODES.SAAS_SUPPORT) {
            adSyncEndPoint = '/Settings/SyncActiveDirectorySaas';
        }
        try {
            await httpPost(adSyncEndPoint, { isFullAdSync: isFullAdSync });

            setTimeout(() => {
                this.setState({ ADSyncGenerated: false });
            }, 2500);
        } catch (err) {
            if (err.response.status === 503) {
                // If Adsync in progress.
                setTimeout(() => {
                    this.setState({ ADSyncGenerated: false });
                }, 2500);
            } else {
                this.setState({ ADSyncGenerated: false });
                console.error(err);
            }
        }
    };

    async SendShutdownRequest(shutdownADSync) {
        this.setState({
            skipTimerUpdate: true,
            ShutdownGenerated: true,
            ShutdownError: false,
            RefreshData: false,
            SystemStateInProgress: true
        });

        try {
            await httpPost('/Settings/ShutdownSystem', { shutdownADSync: shutdownADSync });

            const tempNodes = this.state.SettingsData.NodesConfigurations;
            for (const nodeID of Object.keys(tempNodes)) {
                tempNodes[nodeID].state = NODES_STATES.STANDBY.ID;
            }

            this.setState({ NodesConfigurations: tempNodes, RefreshData: true, ShutdownGenerated: false });
            if (shutdownADSync) {
                this.setState({ AutomaticADSyncEnabled: false });
            }
        } catch (err) {
            this.setState({ SystemState: true, ShutdownGenerated: false, ShutdownError: true });
            setTimeout(() => {
                this.setState({ ShutdownError: false });
            }, 2500);
            console.error(err);
        }
    }

    async SendPowerupRequest(powerUpADSync) {
        this.setState({
            skipTimerUpdate: true,
            PowerupGenerated: true,
            PowerupError: false,
            RefreshData: false,
            SystemStateInProgress: true
        });

        try {
            await httpPost('/Settings/PowerupSystem', { powerUpADSync: powerUpADSync });

            if (this.props.systemMode !== SYSTEM_MODES.SAAS) {
                this.actionForAllNodesConfirmation(SYSTEM_STATUS_ACTIONS.START);
            } else {
                const tempNodes = { ...this.state.SettingsData.NodesConfigurations };

                for (const nodeData of Object.values(tempNodes)) {
                    nodeData.state = NODES_STATES.ACTIVE.ID;
                }

                this.setState({
                    NodesConfigurations: tempNodes
                });
            }
            this.setState({ RefreshData: true, PowerupGenerated: false });
            if (powerUpADSync) {
                this.setState({ AutomaticADSyncEnabled: true });
            }
        } catch (err) {
            this.setState({ SystemState: false, PowerupGenerated: false, PowerupError: true });
            setTimeout(() => {
                this.setState({ PowerupError: false });
            }, 2500);
            console.error(err);
        }
    }

    onBackupCreation = async (backupHttpRequest) => {
        try {
            this.setState({ showCreateBackupModal: false, creatingBackup: true });
            await backupHttpRequest;
            this.setState({ creatingBackup: false, finishCreatingBackup: true });
            setTimeout(() => {
                this.setState({ finishCreatingBackup: false });
            }, 5000);
        } catch (err) {
            this.setState({ creatingBackup: false });
        }
    };
    onScheduleBackupCreation = async (backupScheduleHttpRequest, schedulerDescription) => {
        try {
            await backupScheduleHttpRequest();
            this.setState({ showSchedulersBackup: false, showScheduleBackupRollBack: true, schedulerDescription });
            setTimeout(() => {
                this.setState({ showScheduleBackupRollBack: false });
            }, 5000);
        } catch (err) {
            this.setState({ showScheduleBackupRollBack: false });
        }
    };
    rollBackScheduler = async () => {
        try {
            this.setState({ showScheduleBackupRollBack: false, scheduleBackupRollingBack: true });
            await httpPost('/Settings/rollBackBackupScheduler');
            this.setState({ scheduleBackupRollingBack: false });
        } catch (err) {
            console.error(err);
        }
    };
    SendTowerLog = () => {
        this.setState({ IsSendingLog: true, IsSendingLogError: false, showSendLogsMenu: false });
        httpGet('/Settings/SendLogs')
            .then((response) => {
                this.state.logsActions.push(LOGS_ACTIONS.SENDING);
                this.setState({
                    logsActions: this.state.logsActions,
                    IsSendingLog: false,
                    IsSendingLogError: false,
                    logsToSend: Array.from(new Set([...response.data.logsToSend, ...this.state.logsToSend])),
                    logsToSendInProgress: Array.from(
                        new Set([...response.data.logsToSend, ...this.state.logsToSendInProgress])
                    )
                });
            })
            .catch((err) => {
                this.setState({ IsSendingLogError: true, logsToSend: [] });
                setTimeout(() => this.setState({ IsSendingLog: false, IsSendingLogError: false }), 6000);
                console.error('Send tower logs failed', err.toString());
            });
    };
    SendExtendedLog = (anonymized) => {
        this.setState({ IsSendingLog: true, IsSendingLogError: false });
        httpGet('/Settings/SendLogs', {
            [HTTP_OPTIONS.REQ_OPTIONS]: { params: { extended: true, anonymized: anonymized } }
        })
            .then((response) =>
                this.setState({
                    IsSendingLog: false,
                    IsSendingLogError: false,
                    logsToSend: Array.from(new Set([...response.data.logsToSend, ...this.state.logsToSend])),
                    logsToSendInProgress: Array.from(
                        new Set([...response.data.logsToSend, ...this.state.logsToSendInProgress])
                    )
                })
            )
            .catch((err) => {
                this.setState({ IsSendingLogError: true, logsToSend: [] });
                setTimeout(() => this.setState({ IsSendingLog: false, IsSendingLogError: false }), 6000);
                console.error('Send extended logs failed', err.toString());
            });
    };
    switchDbStatistics = () => {
        if (this.state.dbStatisticsChangeLoading) return;

        this.setState({ dbStatisticsChangeLoading: true });
        httpPost('/Settings/switchDbStatisticsCollection')
            .then((response) => {
                this.setState({
                    dbStatisticsEnabled: response.data.dbStatisticsEnabled,
                    dbStatisticsChangeLoading: false
                });
            })
            .catch((err) => {
                this.setState({ dbStatisticsChangeLoading: false });
                console.error('switch db statistics failed', err.toString());
            });
    };
    sendAllLogs = () => {
        this.setState({ IsSendingLog: true, showSendLogsMenu: false });
        httpGet('/Settings/SendAllLogs')
            .then((response) => {
                this.state.logsActions.push(LOGS_ACTIONS.SENDING);
                this.setState({
                    logsActions: this.state.logsActions,
                    IsSendingLogError: false,
                    logsToSend: Array.from(new Set([...response.data.logsToSend, ...this.state.logsToSend])),
                    logsToSendInProgress: Array.from(
                        new Set([...response.data.logsToSend, ...this.state.logsToSendInProgress])
                    )
                });
            })
            .catch((err) => {
                this.setState({ IsSendingLogError: true });
                setTimeout(() => this.setState({ IsSendingLog: false, IsSendingLogError: false }), 6000);
                console.error('Send all logs failed', err.toString());
            });
    };
    sendSelectedNodesLogs = () => {
        httpPost('/Settings/sendSelectedNodesLogs', { selectedNodes: Array.from(this.state.selectedNodes) })
            .then((response) => {
                this.state.logsActions.push(LOGS_ACTIONS.SENDING);
                this.setState({
                    logsActions: this.state.logsActions,
                    logsToSend: Array.from(new Set([...response.data.logsToSend, ...this.state.logsToSend])),
                    logsToSendInProgress: Array.from(
                        new Set([...response.data.logsToSend, ...this.state.logsToSendInProgress])
                    )
                });
            })
            .catch((err) => {
                console.error('Send all selected nodes logs failed', err.toString());
            });
    };
    sendSelectedDcsLogs = () => {
        httpPost('/Settings/sendSelectedDcsLogs', { selectedDcs: Array.from(this.state.selectedDcs) })
            .then((response) => {
                this.state.logsActions.push(LOGS_ACTIONS.SENDING);
                this.setState({
                    logsActions: this.state.logsActions,
                    logsToSend: Array.from(new Set([...response.data.logsToSend, ...this.state.logsToSend])),
                    logsToSendInProgress: Array.from(
                        new Set([...response.data.logsToSend, ...this.state.logsToSendInProgress])
                    )
                });
            })
            .catch((err) => {
                console.error('Send all selected DCs logs failed', err.toString());
            });
    };
    sendSelectedPushServicesLogs = () => {
        httpPost('/Settings/sendSelectedPushServicesLogs', {
            selectedPushServices: Array.from(this.state.selectedPushServices)
        })
            .then((response) => {
                this.state.logsActions.push(LOGS_ACTIONS.SENDING);
                this.setState({
                    logsActions: this.state.logsActions,
                    logsToSend: Array.from(new Set([...response.data.logsToSend, ...this.state.logsToSend])),
                    logsToSendInProgress: Array.from(
                        new Set([...response.data.logsToSend, ...this.state.logsToSendInProgress])
                    )
                });
            })
            .catch((err) => {
                console.error('Send all selected Push Services logs failed', err.toString());
            });
    };
    sendSelectedAdConnectsLogs = () => {
        httpPost('/Settings/sendSelectedAdConnectsLogs', {
            selectedAdConnects: Array.from(this.state.selectedAdConnects)
        })
            .then((response) => {
                this.state.logsActions.push(LOGS_ACTIONS.SENDING);
                this.setState({
                    logsActions: this.state.logsActions,
                    logsToSend: Array.from(new Set([...response.data.logsToSend, ...this.state.logsToSend])),
                    logsToSendInProgress: Array.from(
                        new Set([...response.data.logsToSend, ...this.state.logsToSendInProgress])
                    )
                });
            })
            .catch((err) => {
                console.error('Send all selected Ad Connect logs failed', err.toString());
            });
    };

    isSendingRequestInProgress = () => {
        const isAllLogsSent = !this.state.logsToSend.some((log) =>
            this.state.logsToSendInProgress.find((logInProgress) => logInProgress === log)
        );
        if (isAllLogsSent && this.state.logsToSend.length) {
            const newLogsActions = this.state.logsActions.filter((action) => action !== LOGS_ACTIONS.SENDING);
            newLogsActions.push(LOGS_ACTIONS.SENDING_ENDED);
            this.setState({
                logsToSend: [],
                logsActions: newLogsActions,
                logSentNumber: this.state.logsToSend.length
            });
            setTimeout(() => {
                this.setState({
                    logsActions: this.state.logsActions.filter((action) => action !== LOGS_ACTIONS.SENDING_ENDED)
                });
            }, ENDED_LOG_ACTION_TIME_OUT);
        }
    };

    isGeneratingRequestInProgress = () => {
        const isAllLogsGenerated = !this.state.logsToGenerate.some((log) =>
            this.state.logsToGenerateInProgress.find((logInProgress) => logInProgress === log)
        );
        if (isAllLogsGenerated && this.state.logsToGenerate.length) {
            const newLogsActions = this.state.logsActions.filter((action) => action !== LOGS_ACTIONS.GENERATING);
            newLogsActions.push(LOGS_ACTIONS.GENERATING_ENDED);
            this.setState({
                logsToGenerate: [],
                logsActions: newLogsActions,
                logGeneratedNumber: this.state.logsToGenerate.length
            });
            setTimeout(() => {
                this.setState({
                    logsActions: this.state.logsActions.filter((action) => action !== LOGS_ACTIONS.GENERATING_ENDED)
                });
            }, ENDED_LOG_ACTION_TIME_OUT);
        }
    };

    generateAdminConsoleLogs = () => {
        httpPost('/Settings/generateAdminConsoleLogs')
            .then((response) => {
                this.state.logsActions.push(LOGS_ACTIONS.GENERATING);
                this.setState({
                    logsActions: this.state.logsActions,
                    logsToGenerate: Array.from(
                        new Set([...response.data.logsToGenerate, ...this.state.logsToGenerate])
                    ),
                    logsToGenerateInProgress: Array.from(
                        new Set([...response.data.logsToGenerate, ...this.state.logsToGenerateInProgress])
                    )
                });
            })
            .catch((err) => {
                console.error('generate Admin console logs failed', err.toString());
            });
    };

    generateSelectedNodesLogs = () => {
        httpPost('/Settings/generateSelectedNodesLogs', { selectedNodes: Array.from(this.state.selectedNodes) })
            .then((response) => {
                this.state.logsActions.push(LOGS_ACTIONS.GENERATING);
                this.setState({
                    logsActions: this.state.logsActions,
                    logsToGenerate: Array.from(
                        new Set([...response.data.logsToGenerate, ...this.state.logsToGenerate])
                    ),
                    logsToGenerateInProgress: Array.from(
                        new Set([...response.data.logsToGenerate, ...this.state.logsToGenerateInProgress])
                    )
                });
            })
            .catch((err) => {
                console.error('generate all selected nodes logs failed', err.toString());
            });
    };
    generateSelectedDcsLogs = () => {
        httpPost('/Settings/generateSelectedDcsLogs', { selectedDcs: Array.from(this.state.selectedDcs) })
            .then((response) => {
                this.state.logsActions.push(LOGS_ACTIONS.GENERATING);
                this.setState({
                    logsActions: this.state.logsActions,
                    logsToGenerate: Array.from(
                        new Set([...response.data.logsToGenerate, ...this.state.logsToGenerate])
                    ),
                    logsToGenerateInProgress: Array.from(
                        new Set([...response.data.logsToGenerate, ...this.state.logsToGenerateInProgress])
                    )
                });
            })
            .catch((err) => {
                console.error('generate all selected DCs logs failed', err.toString());
            });
    };
    generateSelectedPushServicesLogs = () => {
        httpPost('/Settings/generateSelectedPushServicesLogs', {
            selectedPushServices: Array.from(this.state.selectedPushServices)
        })
            .then((response) => {
                this.state.logsActions.push(LOGS_ACTIONS.GENERATING);
                this.setState({
                    logsActions: this.state.logsActions,
                    logsToGenerate: Array.from(
                        new Set([...response.data.logsToGenerate, ...this.state.logsToGenerate])
                    ),
                    logsToGenerateInProgress: Array.from(
                        new Set([...response.data.logsToGenerate, ...this.state.logsToGenerateInProgress])
                    )
                });
            })
            .catch((err) => {
                console.error('generate all selected Push Services logs failed', err.toString());
            });
    };

    onLogsManagerLogsGeneration = (logsToGenerate) => {
        this.state.logsActions.push(LOGS_ACTIONS.GENERATING);
        this.setState({
            logsActions: this.state.logsActions,
            logsToGenerate: Array.from(new Set([...logsToGenerate, ...this.state.logsToGenerate])),
            logsToGenerateInProgress: Array.from(new Set([...logsToGenerate, ...this.state.logsToGenerateInProgress]))
        });
        this.RefreshSystemStatus();
    };

    ToggleOperationsDropdown = (event) => {
        if (!event) {
            this.setState({
                showSendLogsMenu: false,
                showDownloadExtendedDiagnosticsMenu: false,
                showGenerateLogsMenu: false,
                showDownloadLogsMenu: false
            });
        }
    };
    ToggleSendLogsHandler = (event) => {
        event.stopPropagation();
        if (!this.state.IsSendingLog) {
            this.setState({
                showSendLogsMenu: !this.state.showSendLogsMenu,
                showDownloadExtendedDiagnosticsMenu: false,
                showGenerateLogsMenu: false,
                showDownloadLogsMenu: false
            });
        }
    };
    ToggleDownloadExtendedHandler = (event) => {
        event.stopPropagation();
        this.setState({
            showSendLogsMenu: false,
            showDownloadExtendedDiagnosticsMenu: !this.state.showDownloadExtendedDiagnosticsMenu,
            showGenerateLogsMenu: false,
            showDownloadLogsMenu: false
        });
    };
    ToggleGenerateLogsHandler = (event) => {
        event.stopPropagation();
        this.setState({
            showSendLogsMenu: false,
            showDownloadExtendedDiagnosticsMenu: false,
            showGenerateLogsMenu: !this.state.showGenerateLogsMenu,
            showDownloadLogsMenu: false
        });
    };
    ToggleDownloadLogsHandler = (event) => {
        event.stopPropagation();
        this.setState({
            showSendLogsMenu: false,
            showDownloadExtendedDiagnosticsMenu: false,
            showGenerateLogsMenu: false,
            showDownloadLogsMenu: !this.state.showDownloadLogsMenu
        });
    };
    ClickedOutsideHandler = () => {
        if (this.state.showSendLogsMenu) {
            this.setState({
                showSendLogsMenu: false,
                showDownloadExtendedDiagnosticsMenu: false,
                showGenerateLogsMenu: false,
                showDownloadLogsMenu: false
            });
        }
    };
    SendAnonymizedTowerData = () => {
        this.SendExtendedLog(true);
    };
    SendPlainTowerData = () => {
        this.SendExtendedLog(false);
    };
    closeConfirmation = () => {
        this.setState({ confirmationMessage: { ...this.state.confirmationMessage, showConfirmation: false } });
    };
    ChangeSilverfortStatus = (cb, changeADSyncState) => {
        this.setState({ confirmationMessage: { ...this.state.confirmationMessage, showConfirmation: false } });
        if (this.state.NewStateUp) {
            this.SendPowerupRequest(changeADSyncState);
        } else {
            this.SendShutdownRequest(changeADSyncState);
        }
    };
    changeNodeDesiredState = (nodeID, desiredState) => {
        const tempNodes = { ...this.state.SettingsData.NodesConfigurations };
        if (tempNodes[nodeID]) {
            tempNodes[nodeID].state = desiredState;
        }

        this.setState({ NodesConfigurations: tempNodes });
    };
    disabledRebalanceAzForNodes = (nodeIds, isDisabled) => {
        const tmpRebalanceAzDisabledNodes = [];
        for (const nodeId of this.state.rebalanceAzDisabledNodes) {
            if (!nodeIds.includes(nodeId)) {
                tmpRebalanceAzDisabledNodes.push(nodeId);
            }
        }
        if (isDisabled) {
            for (const nodeId of nodeIds) {
                tmpRebalanceAzDisabledNodes.push(nodeId);
            }
        }
        this.setState({ rebalanceAzDisabledNodes: tmpRebalanceAzDisabledNodes }, this.updateNodeRows);
    };
    tabSelectedHandler = (selectedTab) => {
        if (this.state.selectedTab !== selectedTab) {
            this.setState(
                {
                    selectedTab: selectedTab,
                    selectedDcs: new Set(),
                    selectedNodes: new Set(),
                    selectedPushServices: new Set(),
                    selectedAdConnects: new Set(),
                    isAdminConsoleSelected: false
                },
                () => {
                    this.updateAdConnectRows();
                    this.updateAdminConsoleRows();
                    this.updateDcsRows();
                    this.updatePushServicesRows();
                    this.updateNodeRows();
                }
            );
        }
    };

    actionForAllNodesConfirmation = (actionType) => {
        const actionName = uppercaseFirstLetter(actionType);
        this.setState({
            confirmationMessage: {
                showConfirmation: true,
                title: `${actionName} all nodes`,
                body: `Are you sure you want to ${actionType} all Silverfort's nodes?`,
                approveText: actionName,
                onCancel: this.closeActionsConfirmation,
                onApprove: () => this.handleActionForAllNodes(actionType)
            }
        });
    };

    actionForAllNodesEnabled = (actionType) => {
        let result = true;
        if (actionType === SYSTEM_STATUS_ACTIONS.START) {
            result = Object.values(this.state.Nodes).some((node) => {
                return (
                    node.status === NODES_STATES.STANDBY.ID &&
                    (this.props.systemMode !== SYSTEM_MODES.ON_PREM ||
                        (node.domainsConfiguration && Object.keys(node.domainsConfiguration).length > 0))
                );
            });
        } else if (actionType === SYSTEM_STATUS_ACTIONS.RECOVER) {
            result = Object.values(this.state.Nodes).some((node) => {
                return node.status === NODES_STATES.ACTIVE_WITH_INCIDENTS.ID;
            });
        }

        return result;
    };

    recoverAllDcsConfirmation = () => {
        this.setState({
            confirmationMessage: {
                showConfirmation: true,
                title: 'Recover all domain controllers',
                body: `Are you sure you want to recover all domain controllers?`,
                approveText: 'Recover',
                onCancel: this.closeActionsConfirmation,
                onApprove: this.recoverAllDcs
            }
        });
    };
    closeActionsConfirmation = () => {
        this.setState({
            confirmationMessage: { ...this.state.confirmationMessage, showConfirmation: false }
        });
    };

    handleActionForAllNodes = async (actionType) => {
        try {
            const url = actionType === 'recover' ? '/Settings/RecoverAllNodes' : '/Settings/PowerupAllNodes';
            await httpPost(url);
            const tempNodes = { ...this.state.SettingsData.NodesConfigurations };

            for (const nodeData of Object.values(tempNodes)) {
                nodeData.state = NODES_STATES.ACTIVE.ID;
            }

            this.setState({
                NodesConfigurations: tempNodes,
                confirmationMessage: { ...this.state.confirmationMessage, showConfirmation: false }
            });
        } catch (err) {
            this.setState({ ADSyncGenerated: false });
            console.error(err);
        }
    };

    recoverAllDcs = async () => {
        this.setState({ allDcsDuringRecover: true });
        try {
            await httpPost('/Settings/RecoverAllDcs');

            setTimeout(() => {
                this.setState({ allDcsDuringRecover: false });
            }, 2000);

            this.setState({
                confirmationMessage: { ...this.state.confirmationMessage, showConfirmation: false }
            });
        } catch (err) {
            this.setState({ allDcsDuringRecover: false });
            console.error(err);
        }
    };

    sortNodes = (nodes) => {
        return nodes.sort(function (a, b) {
            try {
                const x = a.name.toLowerCase();
                const y = b.name.toLowerCase();
                if (x < y) {
                    return -1;
                }
                if (x > y) {
                    return 1;
                }
                return 0;
            } catch (ex) {
                return 0;
            }
        });
    };

    isAllSelected = (filterIds, selectedIds) => {
        return filterIds.every((id) => selectedIds.has(id));
    };

    selectAll = (selectedIds, filterIds) => {
        const newSelectedIds = new Set(selectedIds);
        if (!filterIds.length) {
            return newSelectedIds;
        }

        if (this.isAllSelected(filterIds, newSelectedIds)) {
            for (const nodeId of filterIds) {
                newSelectedIds.delete(nodeId);
            }
        } else {
            for (const nodeId of filterIds) {
                newSelectedIds.add(nodeId);
            }
        }

        if (!newSelectedIds.size) {
            this.setState({
                logsActions: this.state.logsActions.filter((action) => action !== LOGS_ACTIONS.SELECTING)
            });
        } else if (this.state.logsActions[this.state.logsActions.length - 1] !== LOGS_ACTIONS.SELECTING) {
            this.state.logsActions.push(LOGS_ACTIONS.SELECTING);
        }

        return newSelectedIds;
    };

    updateNodeRows = () => {
        const hasServiceBus = isServiceBusEnabled(this.state.SettingsData);
        const nodeRows = [];
        const filterNodesIds = [];
        let activeNodes = 0;
        const sortedNodes = this.sortNodes(this.state.Nodes);

        Object.keys(sortedNodes).forEach((nodeDataIndex) => {
            const nodeData = sortedNodes[nodeDataIndex];
            if (!this.state.SettingsData.NodesConfigurations[nodeData.id]) return;

            const domains = Object.keys(this.state.SettingsData.DomainsConfigurations);
            const ADSyncDomainNodes = domains.reduce(
                (set, domain) => set.add(...this.state.SettingsData.DomainsConfigurations[domain].ADSyncDomainNodes),
                new Set()
            );

            const nodeName = this.state.SettingsData.NodesConfigurations[nodeData.id].Name || nodeData.name;

            const isFilteredNode = Object.keys(this.state.filters[SYSTEM_STATUS_TABS.NODES]).every((filterName) => {
                const filterValue = this.state.filters[SYSTEM_STATUS_TABS.NODES][filterName].value;
                if (filterValue === '') {
                    return true;
                }
                switch (filterName) {
                    case 'nodeName':
                        if (filterValue.length > 0 && !filterValue.includes(nodeData.id)) {
                            return false;
                        }
                        break;
                    case 'connectedDcs':
                        if (filterValue.length > 0 && !nodeData.dcs.some((dc) => filterValue.includes(dc.guid))) {
                            return false;
                        }
                        break;
                    case 'domainName':
                        if (
                            filterValue.length > 0 &&
                            !filterValue.some((guidsList) => guidsList.includes(nodeData.id))
                        ) {
                            return false;
                        }
                        break;
                    case 'nodeStatus':
                        if (!filterValue.includes(nodeData.status)) {
                            return false;
                        }
                        break;
                    default:
                        break;
                }
                return true;
            });

            if (isFilteredNode) {
                if (
                    nodeData.status === NODES_STATES.ACTIVE.ID ||
                    nodeData.status === NODES_STATES.ACTIVE_WITH_INCIDENTS.ID
                ) {
                    activeNodes += 1;
                }

                filterNodesIds.push(nodeData.id);
                nodeRows.push(
                    <NodeStatusRow
                        systemMode={this.props.systemMode}
                        isCloudOnly={this.props.isCloudOnly}
                        NodeData={nodeData}
                        key={'node_' + nodeData.id}
                        checkNode={() => {
                            this.checkNode(nodeData.id);
                        }}
                        isSelected={this.state.selectedNodes.has(nodeData.id)}
                        LatestVersions={this.state.SettingsData.LatestVersions}
                        NodeDisplayName={nodeName}
                        changeNodeDesiredState={this.changeNodeDesiredState}
                        NodeDesiredState={this.state.SettingsData.NodesConfigurations[nodeData.id].state}
                        sendingStatus={this.state.SendQueueStats.some((id) => id === nodeData.id)}
                        LicenseExpiresInHours={this.state.LicenseExpiresInHours}
                        csrf={this.state.csrf}
                        allDcsDuringRecover={this.state.allDcsDuringRecover}
                        isEditor={this.state.isEditor}
                        canPreformAdsync={this.state.canPreformAdsync}
                        domainControllers={this.state.DomainControllers}
                        logsWarningDaysDiff={this.state.logsWarningDaysDiff}
                        offlineMode={this.state.SettingsData.OfflineMode}
                        isDuringPolicyVersionChange={this.state.isDuringPolicyVersionChange}
                        disabledRebalanceAzForNodes={this.disabledRebalanceAzForNodes}
                        rebalanceAzActionDisabled={this.state.rebalanceAzDisabledNodes.includes(nodeData.id)}
                        adSyncEnabled={ADSyncDomainNodes.has(nodeData.id)}
                        isMfaOrPasEnabledByLicense={this.props.isMfaOrPasEnabledByLicense}
                        isPoliciesAndAdDataColumnsEnabledByLicense={
                            this.props.isPoliciesAndAdDataColumnsEnabledByLicense
                        }
                        hasServiceBus={hasServiceBus}
                    />
                );
            }
        });
        this.setState({ nodeRows: nodeRows, activeNodes: activeNodes, filterNodesIds: filterNodesIds });
    };

    checkAllNodes = () => {
        const newSelectedNodes = this.selectAll(this.state.selectedNodes, this.state.filterNodesIds);
        this.setState({ selectedNodes: newSelectedNodes }, this.updateNodeRows);
    };

    checkNode = (nodeId) => {
        const newSelectedNodes = new Set(this.state.selectedNodes);
        let newLogsActions = [...this.state.logsActions];
        if (newSelectedNodes.has(nodeId)) {
            newSelectedNodes.delete(nodeId);
            if (!newSelectedNodes.size) {
                newLogsActions = newLogsActions.filter((action) => action !== LOGS_ACTIONS.SELECTING);
            }
        } else {
            newSelectedNodes.add(nodeId);
            if (newLogsActions[newLogsActions.length - 1] !== LOGS_ACTIONS.SELECTING) {
                newLogsActions.push(LOGS_ACTIONS.SELECTING);
            }
        }

        this.setState({ logsActions: newLogsActions, selectedNodes: newSelectedNodes }, this.updateNodeRows);
    };

    updateDcsRows = () => {
        const dcsRows = [];
        const filterDcsIds = [];
        let dcWaitingForRecover = false;
        let activeDcs = 0;
        const sortedDcs = sortBy(this.state.DomainControllers, ['display_name']);
        Object.keys(sortedDcs).forEach((dcDataIndex) => {
            const dcData = sortedDcs[dcDataIndex];
            const dcName = dcData.dc_name || dcData.display_name;

            const isFilteredDc = Object.keys(this.state.filters[SYSTEM_STATUS_TABS.DCS]).every((filterName) => {
                const filterValue = this.state.filters[SYSTEM_STATUS_TABS.DCS][filterName].value;
                if (filterValue === '') {
                    return true;
                }
                switch (filterName) {
                    case 'dcName':
                        if (filterValue.length > 0 && !filterValue.includes(dcData.guid)) {
                            return false;
                        }
                        break;
                    case 'activeNodes':
                    case 'domainName':
                        if (
                            filterValue.length > 0 &&
                            !filterValue.some((guidsList) => guidsList.includes(dcData.guid))
                        ) {
                            return false;
                        }
                        break;

                    case 'recoverStatus':
                        if (
                            filterValue === 'recoverableDcs' &&
                            (dcData.status === DCS_STATES.CONNECTED || dcData.status === DCS_STATES.DISCONNECTED)
                        ) {
                            return false;
                        }
                        break;
                    case 'isProtected':
                        if (
                            (filterValue === 'protectedDcs' && !dcData.is_protected) ||
                            (filterValue === 'notProtectedDcs' && dcData.is_protected)
                        ) {
                            return false;
                        }
                        break;
                    default:
                        break;
                }
                return true;
            });

            if (isFilteredDc) {
                if (dcData.status === DCS_STATES.CONNECTED || dcData.status === DCS_STATES.CONNECTED_WITH_INCIDENTS) {
                    activeDcs += 1;
                }

                if (
                    dcData.status === DCS_STATES.CONNECTED_WITH_INCIDENTS ||
                    dcData.status === DCS_STATES.DISCONNECTED_WITH_INCIDENTS
                ) {
                    dcWaitingForRecover = true;
                }

                filterDcsIds.push(dcData.guid);
                dcsRows.push(
                    <DCStatusRow
                        systemMode={this.props.systemMode}
                        key={`dc_${dcData.guid}`}
                        name={dcName}
                        data={dcData}
                        csrf={this.state.csrf}
                        checkDc={() => {
                            this.checkDc(dcData.guid);
                        }}
                        isSelected={this.state.selectedDcs.has(dcData.guid)}
                        logsWarningDaysDiff={this.state.logsWarningDaysDiff}
                        allDcsDuringRecover={this.state.allDcsDuringRecover}
                        isEditor={this.state.isEditor}
                        updateDcProtection={this.updateDcProtection}
                        updateDcLogLevel={this.updateDcLogLevel}
                        dcCaptureConfig={this.state.dcCaptureConfig}
                        azConfig={this.state.azConfig}
                        isCloudOnly={this.props.isCloudOnly}
                    />
                );
            }
        });
        this.setState({
            dcsRows: dcsRows,
            activeDcs: activeDcs,
            dcWaitingForRecover: dcWaitingForRecover,
            filterDcsIds: filterDcsIds
        });
    };

    checkAllDcs = () => {
        const newSelectedDcs = this.selectAll(this.state.selectedDcs, this.state.filterDcsIds);
        this.setState({ selectedDcs: newSelectedDcs }, this.updateDcsRows);
    };

    checkDc = (dcGuid) => {
        const newSelectedDcs = new Set(this.state.selectedDcs);
        let newLogsActions = [...this.state.logsActions];
        if (newSelectedDcs.has(dcGuid)) {
            newSelectedDcs.delete(dcGuid);
            if (!newSelectedDcs.size) {
                newLogsActions = newLogsActions.filter((action) => action !== LOGS_ACTIONS.SELECTING);
            }
        } else {
            newSelectedDcs.add(dcGuid);
            if (newLogsActions[newLogsActions.length - 1] !== LOGS_ACTIONS.SELECTING) {
                newLogsActions.push(LOGS_ACTIONS.SELECTING);
            }
        }
        this.setState({ selectedDcs: newSelectedDcs, logsActions: newLogsActions }, this.updateDcsRows);
    };

    updatePushServicesRows = () => {
        const pushServicesRows = [];
        const filterPushServicesIds = [];
        let activePushServices = 0;
        const sortedPushServices = this.sortNodes(this.state.PushServices);

        for (const pushService of sortedPushServices) {
            const isFilteredPushService = Object.keys(this.state.filters[SYSTEM_STATUS_TABS.PUSH_SERVICES]).every(
                (filterName) => {
                    const filterValue = this.state.filters[SYSTEM_STATUS_TABS.PUSH_SERVICES][filterName].value;
                    if (filterValue === '') {
                        return true;
                    }
                    switch (filterName) {
                        case 'pushServiceName':
                            if (filterValue.length > 0 && !filterValue.includes(pushService.id)) {
                                return false;
                            }
                            break;
                        default:
                            break;
                    }
                    return true;
                }
            );

            if (isFilteredPushService) {
                if (pushService.status === PUSH_SERVICES_STATUS.CONNECTED) {
                    activePushServices++;
                }
                filterPushServicesIds.push(pushService.id);
                pushServicesRows.push(
                    <PushServiceStatusRow
                        key={'push_service_' + pushService.id}
                        name={pushService.name}
                        data={pushService}
                        csrf={this.state.csrf}
                        allDcsDuringRecover={this.state.allDcsDuringRecover}
                        isEditor={this.state.isEditor}
                        logsWarningDaysDiff={this.state.logsWarningDaysDiff}
                        checkPushService={() => {
                            this.checkPushService(pushService.id);
                        }}
                        isSelected={this.state.selectedPushServices.has(pushService.id)}
                    />
                );
            }
        }

        this.setState({
            pushServicesRows: pushServicesRows,
            activePushServices: activePushServices,
            filterPushServicesIds: filterPushServicesIds
        });
    };

    checkAllPushServices = () => {
        const newSelectedPushServices = this.selectAll(
            this.state.selectedPushServices,
            this.state.filterPushServicesIds
        );
        this.setState({ selectedPushServices: newSelectedPushServices }, this.updatePushServicesRows);
    };

    checkPushService = (pushServiceId) => {
        const newSelectedPushServices = new Set(this.state.selectedPushServices);
        let newLogsAction = [...this.state.logsActions];
        if (newSelectedPushServices.has(pushServiceId)) {
            newSelectedPushServices.delete(pushServiceId);
            if (!newSelectedPushServices.size) {
                newLogsAction = newLogsAction.filter((action) => action !== LOGS_ACTIONS.SELECTING);
            }
        } else {
            newSelectedPushServices.add(pushServiceId);
            if (newLogsAction[newLogsAction.length - 1] !== LOGS_ACTIONS.SELECTING) {
                newLogsAction.push(LOGS_ACTIONS.SELECTING);
            }
        }
        this.setState(
            { selectedPushServices: newSelectedPushServices, logsActions: newLogsAction },
            this.updatePushServicesRows
        );
    };

    updateAdConnectRows = () => {
        const adConnectRows = [];
        const filterAdConnectsIds = [];
        let activeAdConnecters = 0;
        const sortedAdConnecters = sortBy(this.state.adConnectDisplayData, ['hostName']);

        for (const adConnect of sortedAdConnecters) {
            const isFilteredAdConnect = Object.keys(this.state.filters[SYSTEM_STATUS_TABS.AD_CONNECT]).every(
                (filterName) => {
                    const filterValue = this.state.filters[SYSTEM_STATUS_TABS.AD_CONNECT][filterName].value;
                    if (filterValue === '') {
                        return true;
                    }
                    switch (filterName) {
                        case 'adConnectName':
                            if (filterValue.length > 0 && !filterValue.includes(adConnect.guid)) {
                                return false;
                            }
                            break;
                        default:
                            break;
                    }
                    return true;
                }
            );
            if (isFilteredAdConnect) {
                if (adConnect.status !== AD_CONNECT_STATUSES.UNREACHABLE) {
                    activeAdConnecters++;
                }
                filterAdConnectsIds.push(adConnect.guid);
                adConnectRows.push(
                    <AdConnectStatusRow
                        key={`${adConnect.guid}`}
                        domain={adConnect.domain}
                        hostName={adConnect.hostName}
                        status={adConnect.status}
                        version={adConnect.version}
                        guid={adConnect.guid}
                        checkAdConnect={() => {
                            this.checkAdConnect(adConnect.guid);
                        }}
                        isSelected={this.state.selectedAdConnects.has(adConnect.guid)}
                    />
                );
            }
        }
        this.setState({ adConnectRows: adConnectRows, activeAdConnecters: activeAdConnecters });
    };

    checkAllAdConnects = () => {
        const newSelectedAdConnects = this.selectAll(this.state.selectedAdConnects, this.state.filterAdConnectsIds);
        this.setState({ selectedAdConnects: newSelectedAdConnects }, this.updateAdConnectRows);
    };

    checkAdConnect = (adConnectGuid) => {
        const newSelectedAdConnects = new Set(this.state.selectedAdConnects);
        let newLogsAction = [...this.state.logsActions];
        if (newSelectedAdConnects.has(adConnectGuid)) {
            newSelectedAdConnects.delete(adConnectGuid);
            if (!newSelectedAdConnects.size) {
                newLogsAction = newLogsAction.filter((action) => action !== LOGS_ACTIONS.SELECTING);
            }
        } else {
            newSelectedAdConnects.add(adConnectGuid);
            if (newLogsAction[newLogsAction.length - 1] !== LOGS_ACTIONS.SELECTING) {
                newLogsAction.push(LOGS_ACTIONS.SELECTING);
            }
        }
        this.setState(
            { logsActions: newLogsAction, selectedAdConnects: newSelectedAdConnects },
            this.updateAdConnectRows
        );
    };

    updateAdminConsoleRows = () => {
        const hasServiceBus = isServiceBusEnabled(this.state.SettingsData);
        this.setState({
            adminConsoleRows: [
                <AdminConsoleStatusRow
                    key={'towerRow'}
                    systemMode={this.props.systemMode}
                    checkAdminConsole={this.checkAdminConsole}
                    generateAdminConsoleLogs={this.generateAdminConsoleLogs}
                    isAdminConsoleSelected={this.state.isAdminConsoleSelected}
                    isGeneratingPlatformLogs={this.state.isGeneratingPlatformLogs}
                    isPlatformLogExists={this.state.isPlatformLogExists}
                    adminConsoleData={{
                        isGeneratingLog: this.state.isGeneratingAdminConsoleLogs,
                        lastLog: this.state.lastTowerLogGenerated,
                        version: this.state.TowerVersion,
                        isRavenConnected: this.state.isRavenConnected,
                        offlineMode: this.state.SettingsData.OfflineMode,
                        isAryaEnabled: this.state.isAryaEnabled,
                        isAryaConnected: this.state.isAryaConnected,
                        isPhoenixConnected: this.state.isPhoenixConnected
                    }}
                    isMfaOrPasEnabledByLicense={this.props.isMfaOrPasEnabledByLicense}
                    hasServiceBus={hasServiceBus}
                />
            ]
        });
    };

    checkAdminConsole = () => {
        let newLogsActions = [...this.state.logsActions];
        const isAdminConsoleSelected = !this.state.isAdminConsoleSelected;
        if (isAdminConsoleSelected) {
            if (newLogsActions[newLogsActions.length - 1] !== LOGS_ACTIONS.SELECTING) {
                newLogsActions.push(LOGS_ACTIONS.SELECTING);
            }
        } else {
            newLogsActions = newLogsActions.filter((action) => action !== LOGS_ACTIONS.SELECTING);
        }
        this.setState(
            { isAdminConsoleSelected: isAdminConsoleSelected, logsActions: newLogsActions },
            this.updateAdminConsoleRows
        );
    };

    getDomainsAdSyncCards = () => {
        const domainCards = [];
        if (this.state.SettingsData && this.state.SettingsData.DomainsConfigurations) {
            const failedAdSyncDomains = Object.values(this.state.SettingsData.DomainsConfigurations).filter(
                (d) => d.ADSyncFailed
            );
            const successAdSyncDomains = Object.values(this.state.SettingsData.DomainsConfigurations).filter(
                (d) => !d.ADSyncFailed
            );
            this.addDomainCards(domainCards, failedAdSyncDomains);
            this.addDomainCards(domainCards, successAdSyncDomains);
        }

        return domainCards;
    };

    addDomainCards = (domainCards, domains) => {
        for (const domainData of domains) {
            const syncTime = moment(domainData.LastADSync).local();

            domainCards.push(
                <div className="lastSync" id={'domain_card_' + domainData.id} key={'domain_card_' + domainData.id}>
                    <img
                        className="adSyncStatusIcon"
                        src={domainData.ADSyncFailed ? adSyncFailedIcon : adSyncSuccessIcon}
                        alt=""
                    />
                    <div className="lastSyncData">
                        <div className="domainName">{domainData.DomainName}</div>
                        Last AD Sync:
                        <div>
                            {domainData.LastADSync ? syncTime.format('L') + ', ' + syncTime.format('LTS') : 'N/A'}
                        </div>
                    </div>
                </div>
            );
        }
    };

    updateFilter = (fid, newVal) => {
        if (!Object.keys(this.state.filters[this.state.selectedTab]).includes(fid) || newVal === null) {
            return;
        }
        this.state.filters[this.state.selectedTab][fid].value = newVal;
        if (this.state.selectedTab === SYSTEM_STATUS_TABS.NODES) {
            this.updateNodeRows();
        } else if (this.state.selectedTab === SYSTEM_STATUS_TABS.DCS) {
            this.updateDcsRows();
        } else if (this.state.selectedTab === SYSTEM_STATUS_TABS.PUSH_SERVICES) {
            this.updatePushServicesRows();
        } else if (this.state.selectedTab === SYSTEM_STATUS_TABS.AD_CONNECT) {
            this.updateAdConnectRows();
        } else if (this.state.selectedTab === SYSTEM_STATUS_TABS.ADMIN_CONSOLE) {
            this.updateAdminConsoleRows();
        }
    };

    updateDcProtection = (dcGuid, isProtected) => {
        const domainControllers = [...this.state.DomainControllers];
        const dcIndex = domainControllers.findIndex((dc) => dc.guid === dcGuid);
        if (dcIndex >= 0) {
            domainControllers[dcIndex].is_protected = isProtected;
            this.setState({ domainControllers: domainControllers });
        }
    };

    updateDcLogLevel = (dcGuid, logLevel) => {
        const domainControllers = [...this.state.DomainControllers];
        const dcIndex = domainControllers.findIndex((dc) => dc.guid === dcGuid);
        if (dcIndex >= 0) {
            domainControllers[dcIndex].log_level = logLevel;
            this.setState({ domainControllers: domainControllers });
        }
    };

    updateShowErrorDot = () => {
        const showErrorDot = this.state.DomainControllers.some((dc) => {
            return (
                (dc.status === DCS_STATES.DISCONNECTED || dc.status === DCS_STATES.DISCONNECTED_WITH_INCIDENTS) &&
                dc.is_protected !== false
            );
        });
        const showPushServicesErrorDot = this.state.PushServices.some((ps) => {
            return ps.status === PUSH_SERVICES_STATUS.DISCONNECTED;
        });
        const showNodesWarningDot = this.state.Nodes.some((node) => {
            return node.hasWarningDot;
        });
        this.setState({
            showErrorDot: showErrorDot,
            showPushServicesErrorDot: showPushServicesErrorDot,
            showNodesWarningDot: showNodesWarningDot
        });
    };

    getNewValueByOptions = (currentVal, newOptions) => {
        const newVal = [];
        for (const selectValue of currentVal) {
            const selectedOption = newOptions.find((option) => {
                return (
                    option.value.length === selectValue.length &&
                    option.value.every((optionVal) => selectValue.includes(optionVal))
                );
            });
            if (selectedOption) {
                newVal.push(selectedOption.value);
            }
        }
        return newVal;
    };

    updateFiltersOptions = () => {
        const nodesNamesOptions = [];
        const connectedDcsOptions = [];
        const nodesDomainNamesOptions = [];
        const dcNameOptions = [];
        const activeNodesOptions = [];
        const domainNamesOptions = [];
        const pushServicesNamesOptions = [];
        const adConnectHostNameOptions = [];

        const domainNamesToNodes = {};
        const activeNodeNameToDcs = { None: [] };
        const domainNamesToDcs = {};

        for (const node of this.state.Nodes) {
            const nodeName = this.state.SettingsData.NodesConfigurations[node.id].Name || node.name;

            nodesNamesOptions.push({ value: node.id, label: nodeName });
            for (const dcData of Object.values(node.dcs)) {
                if (dcData.status === DCS_STATES.CONNECTED || dcData.status === DCS_STATES.CONNECTED_WITH_INCIDENTS) {
                    const dcName = dcData.dc_name || dcData.display_name;
                    connectedDcsOptions.push({ value: dcData.guid, label: dcName });
                }

                if (!domainNamesToNodes[dcData.domain_name]) {
                    domainNamesToNodes[dcData.domain_name] = [];
                }
                domainNamesToNodes[dcData.domain_name].push(node.id);
            }
        }

        for (const dcData of this.state.DomainControllers) {
            const dcName = dcData.dc_name || dcData.display_name;
            dcNameOptions.push({ value: dcData.guid, label: dcName });

            const activeNodeKey = dcData.hasActiveNode ? dcData.name : 'None';
            if (!activeNodeNameToDcs[activeNodeKey]) {
                activeNodeNameToDcs[activeNodeKey] = [];
            }
            activeNodeNameToDcs[activeNodeKey].push(dcData.guid);

            if (!domainNamesToDcs[dcData.domain_name]) {
                domainNamesToDcs[dcData.domain_name] = [];
            }
            domainNamesToDcs[dcData.domain_name].push(dcData.guid);
        }

        for (const pushService of this.state.PushServices) {
            pushServicesNamesOptions.push({ value: pushService.id, label: pushService.name });
        }

        for (const adConnect of this.state.adConnectDisplayData) {
            adConnectHostNameOptions.push({ value: adConnect.guid, label: adConnect.hostName });
        }

        for (const domainNameKey in domainNamesToNodes) {
            nodesDomainNamesOptions.push({ value: domainNamesToNodes[domainNameKey], label: domainNameKey });
        }

        for (const activeNodeKey in activeNodeNameToDcs) {
            if (activeNodeNameToDcs[activeNodeKey].length > 0) {
                activeNodesOptions.push({ value: activeNodeNameToDcs[activeNodeKey], label: activeNodeKey });
            }
        }

        for (const domainNameKey in domainNamesToDcs) {
            domainNamesOptions.push({ value: domainNamesToDcs[domainNameKey], label: domainNameKey });
        }

        const newFilters = this.state.filters;
        const hideNodes = isSystemTabHiddenBySystemMode(
            SYSTEM_STATUS_TABS_TYPES.NODES,
            this.props.systemMode,
            this.props.isCloudOnly
        );
        if (hideNodes) {
            delete newFilters[SYSTEM_STATUS_TABS.DCS].activeNodes;
        } else {
            newFilters[SYSTEM_STATUS_TABS.DCS].activeNodes.filterConfig.options = activeNodesOptions;
        }
        newFilters[SYSTEM_STATUS_TABS.NODES].nodeName.filterConfig.options = nodesNamesOptions;
        newFilters[SYSTEM_STATUS_TABS.NODES].connectedDcs.filterConfig.options = connectedDcsOptions;
        newFilters[SYSTEM_STATUS_TABS.NODES].domainName.filterConfig.options = nodesDomainNamesOptions;
        newFilters[SYSTEM_STATUS_TABS.DCS].dcName.filterConfig.options = dcNameOptions;
        newFilters[SYSTEM_STATUS_TABS.DCS].domainName.filterConfig.options = domainNamesOptions;
        newFilters[SYSTEM_STATUS_TABS.PUSH_SERVICES].pushServiceName.filterConfig.options = pushServicesNamesOptions;
        newFilters[SYSTEM_STATUS_TABS.AD_CONNECT].adConnectName.filterConfig.options = adConnectHostNameOptions;

        if (newFilters[SYSTEM_STATUS_TABS.NODES].domainName.value) {
            const newVal = this.getNewValueByOptions(
                newFilters[SYSTEM_STATUS_TABS.NODES].domainName.value,
                nodesDomainNamesOptions
            );
            newFilters[SYSTEM_STATUS_TABS.NODES].domainName.value = newVal;
        }

        if (!hideNodes && newFilters[SYSTEM_STATUS_TABS.DCS].activeNodes.value) {
            const newVal = this.getNewValueByOptions(
                newFilters[SYSTEM_STATUS_TABS.DCS].activeNodes.value,
                activeNodesOptions
            );
            newFilters[SYSTEM_STATUS_TABS.DCS].activeNodes.value = newVal;
        }

        if (newFilters[SYSTEM_STATUS_TABS.DCS].domainName.value) {
            const newVal = this.getNewValueByOptions(
                newFilters[SYSTEM_STATUS_TABS.DCS].domainName.value,
                domainNamesOptions
            );
            newFilters[SYSTEM_STATUS_TABS.DCS].domainName.value = newVal;
        }

        this.setState({ filters: newFilters });
    };

    render() {
        const canToggleSystemPower = isUserPermitted(this.state.user, 'toggleSystemPower', ROLES.EDITOR.value);
        const offlineMode = this.state.SettingsData && this.state.SettingsData.OfflineMode;
        const systemStatusSystemMode = SYSTEM_MODE_HIDDEN_VIEWS[LAYOUT_TABS.SYSTEM_STATUS.ID][this.props.systemMode];
        const hideLogActions = systemStatusSystemMode.hideLogActions;
        const hideLogsButton = this.props.isCloudOnly;
        const shouldForceBackupForSaasSupport =
            this.props.systemMode === SYSTEM_MODES.SAAS_SUPPORT && this.props.forceBackupForSaasSupport;
        const hideSystemBackup =
            this.props.isCloudOnly || systemStatusSystemMode.hideSystemBackup || !this.props.systemBackupEnabled;
        const hideAdSync = this.props.isCloudOnly;
        const hideConnectivityStatus = this.props.isCloudOnly;
        const hideDisableSilverfortButton = this.props.isCloudOnly;
        const hideOperations = hideAdSync && hideSystemBackup && hideLogsButton;
        const isOnPrem = this.props.systemMode === SYSTEM_MODES.ON_PREM;
        const hasServiceBus = isServiceBusEnabled(this.state.SettingsData);

        return (
            <div id="systemStatus" onClick={this.ClickedOutsideHandler}>
                {this.state.IsLoading && (
                    <div className="silverfort-loader">
                        <div className="logo"></div>
                    </div>
                )}
                <div id="wrapper" className="content">
                    {this.state.showCMSBanner && hasServiceBus && (
                        <div className="flex flex-row w-full h-[48px] bg-[#F5FAFF] border-b border-sf-blue py-0 px-4 m-0 justify-between items-center">
                            <span>
                                Action required: Configure your IPs and URLs to ensure a seamless transition from the
                                Mobile Messaging Service to the Cloud Messaging Service
                            </span>
                            <CancelIcon
                                className="w-[16px] h-[16px] cursor-pointer"
                                fill="color-sf-gray-400 hover:color-sf-gray-300"
                                onClick={() => this.setState({ showCMSBanner: false })}
                            />
                        </div>
                    )}
                </div>

                <div className="contentContainer noscroll">
                    <div className={`system-status ${this.state.showCMSBanner && hasServiceBus ? 'pt-12' : ''}`}>
                        <div className="header hoverBox">
                            <div className="companyRow">
                                <div className="companyLogo">
                                    <img src={this.state.CompanyLogo} alt="" />
                                    <span>{this.state.CompanyName}</span>
                                </div>
                                {this.state.TowerVersion && (
                                    <div className="towerVersion">
                                        <span>{`Version: ${this.state.TowerVersion}`}</span>
                                    </div>
                                )}
                            </div>
                            <div className="statusRow">
                                <div className="leftSide">
                                    <div className="statusText">
                                        <span>System Status:</span>
                                        {this.state.SystemState && (
                                            <div className="status active">
                                                <img src={activeStateIcon} alt="" />
                                                <span>Active</span>
                                            </div>
                                        )}
                                        {!this.state.SystemState && (
                                            <div className="status nonactive flex items-center">
                                                <img src={nonactiveStateIcon} alt="" />
                                                <span>Standby</span>
                                            </div>
                                        )}
                                    </div>
                                    {!hideConnectivityStatus && (
                                        <div className="connectivityStatus">
                                            <div className="statusMobile">
                                                {hasServiceBus && (
                                                    <MmsCmsConnectivityStatus
                                                        name={'MMS'}
                                                        offlineMode={offlineMode}
                                                        isActive={this.state.isRavenConnected}
                                                    />
                                                )}
                                                <MmsCmsConnectivityStatus
                                                    name={'CMS'}
                                                    offlineMode={offlineMode}
                                                    isActive={this.state.isPhoenixConnected}
                                                />
                                            </div>

                                            <FeatureLock
                                                displayOptions={{
                                                    enabled: this.props.isMfaOrPasEnabledByLicense,
                                                    disabledDisplay: DisabledDisplayOptions.HIDDEN
                                                }}>
                                                <>
                                                    {isOnPrem && this.state.isAryaEnabled && (
                                                        <div className="statusPushServices">
                                                            {this.state.isAryaConnected && (
                                                                <div className="status active">
                                                                    <img src={activeSpsIcon} alt="" />
                                                                    <span>
                                                                        On-Prem Desktop Messaging Service available
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {!this.state.isAryaConnected && (
                                                                <div className="status nonactive">
                                                                    <img src={nonactiveSpsIcon} alt="" />
                                                                    <span>
                                                                        On-Prem Desktop Messaging Service unavailable
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            </FeatureLock>
                                        </div>
                                    )}
                                </div>
                                {!hideDisableSilverfortButton && this.state.SystemState && (
                                    <div className="shutdown">
                                        <Tooltip
                                            title={
                                                <div>
                                                    <div>Will change the system mode to standby,</div>
                                                    <div>disabling detection and prevention.</div>
                                                </div>
                                            }
                                            enterDelay={100}
                                            placement={'bottom'}
                                            classes={{ tooltip: 'moreInfoIcon-tooltip' }}>
                                            <div
                                                className={
                                                    'shutdownButton' +
                                                    (this.state.ShutdownGenerated ||
                                                    !canToggleSystemPower ||
                                                    this.state.SystemStateInProgress
                                                        ? ' disabled'
                                                        : '')
                                                }
                                                onClick={() => {
                                                    if (!canToggleSystemPower) {
                                                        return;
                                                    }
                                                    this.setState({
                                                        confirmationMessage: this.getChangeStatusModalDetails(false),
                                                        NewStateUp: false
                                                    });
                                                }}>
                                                <img src={powerIcon} alt="" />
                                                <div>Disable Silverfort</div>
                                            </div>
                                        </Tooltip>
                                        {this.state.ShutdownError && <div className="hint error">Error Occurred!</div>}
                                    </div>
                                )}
                                {!hideDisableSilverfortButton && !this.state.SystemState && (
                                    <div className="powerup">
                                        <div
                                            className={
                                                'powerupButton ' +
                                                (this.state.PowerupGenerated ||
                                                this.state.SystemStateInProgress ||
                                                !canToggleSystemPower ||
                                                (!this.state.LicenseTrialMode && this.state.LicenseExpiresInHours <= 0)
                                                    ? ' disabled'
                                                    : '')
                                            }
                                            onClick={() => {
                                                if (!canToggleSystemPower) {
                                                    return;
                                                }
                                                this.setState({
                                                    confirmationMessage: this.getChangeStatusModalDetails(true),
                                                    NewStateUp: true
                                                });
                                            }}>
                                            <img src={powerIcon} alt="" />
                                            <div>Start Silverfort</div>
                                        </div>
                                        {this.state.PowerupError && <div className="hint error">Error Occurred!</div>}
                                        {!this.state.PowerupError && (
                                            <div
                                                className={
                                                    !this.state.LicenseTrialMode &&
                                                    this.state.LicenseExpiresInHours <= 0
                                                        ? 'hint error'
                                                        : 'hint'
                                                }>
                                                {!this.state.LicenseTrialMode && this.state.LicenseExpiresInHours <= 0
                                                    ? "Can't start Silverfort! License has expired, update the license key in the Settings page."
                                                    : 'Will activate the silverfort servers to enable detection and prevention.'}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {!hideOperations && (
                                <div className="operations">
                                    {!hideAdSync && (
                                        <>
                                            <div className="adSync">
                                                <div className="adSyncButtonContainer">
                                                    <div
                                                        className={
                                                            'adSyncButton' +
                                                            (this.state.ADSyncGenerated || !this.state.canPreformAdsync
                                                                ? ' disabled'
                                                                : '')
                                                        }
                                                        onClick={() => this.SendActiveDirectorySyncRequest(false)}>
                                                        <i className="fa fa-refresh" aria-hidden="true"></i>
                                                        {!this.state.ADSyncGenerated && <div>Quick AD Sync</div>}
                                                        {this.state.ADSyncGenerated && <div>Request Sent!</div>}
                                                    </div>
                                                    <div
                                                        className={
                                                            'adSyncDropdown' +
                                                            (this.state.ADSyncGenerated || !this.state.canPreformAdsync
                                                                ? ' disabled'
                                                                : '')
                                                        }>
                                                        <DropdownButton
                                                            className={`nodeActionsButton`}
                                                            pullRight
                                                            disabled={this.state.ADSyncGenerated}
                                                            id={'action-dropdown'}
                                                            title={''}>
                                                            <MenuItem
                                                                onClick={() => {
                                                                    this.SendActiveDirectorySyncRequest(true);
                                                                }}>
                                                                Full AD Sync
                                                            </MenuItem>
                                                        </DropdownButton>
                                                    </div>
                                                </div>

                                                {this.state.ADSyncError && (
                                                    <span className="error">Error Occurred!</span>
                                                )}

                                                <div className="automaticADSync">
                                                    <span>Automatic AD Sync</span>
                                                    <div
                                                        className={`autoADSyncContainer ${
                                                            !this.state.isEditor ? 'disabled' : ''
                                                        }`}>
                                                        <label
                                                            htmlFor={'autoADSyncContainer'}
                                                            id={'autoADSyncContainer'}
                                                            onClick={() => {
                                                                this.setState({
                                                                    confirmationMessage:
                                                                        this.getAutomaticADSyncModalDetails()
                                                                });
                                                            }}>
                                                            <input
                                                                checked={this.state.AutomaticADSyncEnabled}
                                                                onChange={() => {}}
                                                                className={'switch'}
                                                                type="checkbox"
                                                            />
                                                            <div className={'elements'}>
                                                                <span
                                                                    className={`text ${
                                                                        this.state.AutomaticADSyncEnabled
                                                                            ? 'enable'
                                                                            : 'disable'
                                                                    }`}>
                                                                    {this.state.AutomaticADSyncEnabled
                                                                        ? 'Enabled'
                                                                        : 'Disabled'}
                                                                </span>
                                                                <div></div>
                                                            </div>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="adSyncCards">
                                                <StyledTabs domainsData={this.getDomainsAdSyncCards()} />
                                            </div>
                                        </>
                                    )}

                                    <div className="flex gap-2">
                                        {(!hideSystemBackup || shouldForceBackupForSaasSupport) && (
                                            <div>
                                                <DropdownMenu
                                                    title={
                                                        <Button
                                                            iconLeft={Icons.Settings}
                                                            text="System Backup"
                                                            className={`min-w-[139px] bg-transparent ${
                                                                this.state.backupsMenuOpen
                                                                    ? 'border-sf-gray-200 ring ring-sf-gray-200'
                                                                    : ''
                                                            }`}
                                                            buttonStyle="TertiarySolid"
                                                        />
                                                    }
                                                    onToggle={(open) => this.setState({ backupsMenuOpen: open })}
                                                    pullRight
                                                    outsideClickOnly={false}
                                                    extraDataComponent={
                                                        <div className="max-h-52 overflow-y-scroll">
                                                            <BackupHistory
                                                                isCreatingBackup={this.state.creatingBackup}
                                                            />
                                                        </div>
                                                    }>
                                                    <DropdownMenuItem
                                                        onClick={() => {
                                                            this.setState({ showCreateBackupModal: true });
                                                        }}>
                                                        <span className="flex items-center">
                                                            <Icon icon={Icons.Add} className="w-4 h-4 pr-2" />
                                                            <span>Backup Now</span>
                                                        </span>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        onClick={() => {
                                                            this.setState({ showSchedulersBackup: true });
                                                        }}>
                                                        <span className="flex items-center">
                                                            <Icon icon={Icons.Update} className="w-4 h-4 pr-2" />
                                                            <span>Schedule Backup</span>
                                                        </span>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        onClick={() => this.setState({ showImportBackupModal: true })}>
                                                        <span className="flex items-center">
                                                            <Icon icon={Icons.CloudUpload} className="w-4 h-4 pr-2" />
                                                            <span>Import Backup File</span>
                                                        </span>
                                                    </DropdownMenuItem>
                                                </DropdownMenu>
                                            </div>
                                        )}
                                        {!hideLogsButton && (
                                            <>
                                                <div>
                                                    <DropdownMenu
                                                        title={
                                                            <>
                                                                {(this.state.isNewLogsGenerated ||
                                                                    this.state.isNewLogsSent) && (
                                                                    <div
                                                                        className={`h-3 w-3 bg-sf-blue rounded-full border-[#f0f2f8] border-2 solid block absolute left-4 top-1
                                                            }`}></div>
                                                                )}
                                                                <Button
                                                                    iconLeft={Icons.Settings}
                                                                    text="Logs"
                                                                    className={`pr-3 pl-2 bg-transparent ${
                                                                        this.state.logMenuOpen
                                                                            ? 'border-sf-gray-200 ring ring-sf-gray-200'
                                                                            : ''
                                                                    }`}
                                                                    buttonStyle="TertiarySolid"
                                                                />
                                                            </>
                                                        }
                                                        onToggle={(open) => this.setState({ logMenuOpen: open })}
                                                        pullRight
                                                        outsideClickOnly={false}>
                                                        {!hideLogActions && (
                                                            <DropdownMenuItem
                                                                onClick={() =>
                                                                    this.setState({
                                                                        openLogManager: !this.state.openLogManager,
                                                                        isNewLogsGenerated: false,
                                                                        isNewLogsSent: false
                                                                    })
                                                                }>
                                                                <span className="flex">
                                                                    <img className="pr-2" src={fileIcon} alt="" />
                                                                    {(this.state.isNewLogsGenerated ||
                                                                        this.state.isNewLogsSent) && (
                                                                        <div className="h-3 w-3 bg-sf-blue rounded-full border-white border-2 solid block absolute left-6"></div>
                                                                    )}
                                                                    Log Manager
                                                                </span>
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuItem onClick={this.sendAllLogs}>
                                                            <span className="flex">
                                                                <img className="pr-2" src={cloudUpIcon} alt="" />
                                                                <div className="text-sf">Send All Logs</div>
                                                            </span>
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={this.switchDbStatistics}>
                                                            <span className="flex">
                                                                <LibIcon
                                                                    icon={LibIcons.Server}
                                                                    className={`mr-2 text-sf-gray-400`}
                                                                />
                                                                <div className="text-sf">
                                                                    {this.state.dbStatisticsEnabled
                                                                        ? 'Disable'
                                                                        : 'Enable'}{' '}
                                                                    Database Statistics
                                                                </div>
                                                            </span>
                                                        </DropdownMenuItem>
                                                    </DropdownMenu>
                                                </div>
                                                <div className="relative">
                                                    <div
                                                        className={`${
                                                            this.state.openLogManager ? 'block' : 'hidden'
                                                        } absolute right-0 z-20`}>
                                                        <LogManager
                                                            pushServices={this.state.PushServices}
                                                            nodes={this.state.Nodes}
                                                            domainControllers={this.state.DomainControllers}
                                                            adminConsole={{
                                                                isGeneratingLog:
                                                                    this.state.isGeneratingAdminConsoleLogs,
                                                                lastLog: this.state.lastTowerLogGenerated,
                                                                lastSendLog: this.state.lastTowerLogSent
                                                            }}
                                                            sendAllLogsLastSendingDate={
                                                                this.state.sendAllLogsLastSendingDate
                                                            }
                                                            onExitClick={() => this.setState({ openLogManager: false })}
                                                            nodesConfigurations={
                                                                this.state.SettingsData.NodesConfigurations
                                                            }
                                                            onGenerate={this.onLogsManagerLogsGeneration}
                                                            onDelete={(deleteLogsToDelayTime) =>
                                                                this.setState({ deleteLogsToDelayTime })
                                                            }
                                                            logsToGenerate={this.state.logsToGenerate}
                                                            logsToGenerateInProgress={
                                                                this.state.logsToGenerateInProgress
                                                            }
                                                            logsToSend={this.state.logsToSend}
                                                            logsToSendInProgress={this.state.logsToSendInProgress}
                                                            deleteLogsToDelayTime={this.state.deleteLogsToDelayTime}
                                                            logGeneratedNumber={this.state.logGeneratedNumber}
                                                            logSentNumber={this.state.logSentNumber}
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="serversTabs">
                            <SystemStatusTabs
                                systemMode={this.props.systemMode}
                                selectedTab={this.state.selectedTab}
                                tabSelected={this.tabSelectedHandler}
                                nodes={this.state.nodeRows}
                                checkAllNodes={this.checkAllNodes}
                                isAllNodesSelected={
                                    this.isAllSelected(this.state.filterNodesIds, this.state.selectedNodes) &&
                                    this.state.selectedNodes.size
                                }
                                checkAllDcs={this.checkAllDcs}
                                isAllDcsSelected={
                                    this.isAllSelected(this.state.filterDcsIds, this.state.selectedDcs) &&
                                    this.state.selectedDcs.size
                                }
                                checkAllPushServices={this.checkAllPushServices}
                                isAllPushServicesSelected={
                                    this.isAllSelected(
                                        this.state.filterPushServicesIds,
                                        this.state.selectedPushServices
                                    ) && this.state.selectedPushServices.size
                                }
                                checkAllAdConnects={this.checkAllAdConnects}
                                isAllAdConnectsSelected={
                                    this.isAllSelected(this.state.filterAdConnectsIds, this.state.selectedAdConnects) &&
                                    this.state.selectedAdConnects.size
                                }
                                checkAdminConsole={this.checkAdminConsole}
                                hasServiceBus={hasServiceBus}
                                isAdminConsoleSelected={this.state.isAdminConsoleSelected}
                                activeNodes={this.state.activeNodes}
                                dcs={this.state.dcsRows}
                                activeDcs={this.state.activeDcs}
                                pushServices={this.state.pushServicesRows}
                                activePushServices={this.state.activePushServices}
                                adConnecters={this.state.adConnectRows}
                                activeAdConnecters={this.state.activeAdConnecters}
                                adminConsoleRows={this.state.adminConsoleRows}
                                recoverAllDcs={this.recoverAllDcsConfirmation}
                                actionForAllNodes={this.actionForAllNodesConfirmation}
                                actionForAllNodesEnabled={this.actionForAllNodesEnabled}
                                actionsButtonsEnabled={this.state.SystemState}
                                filters={this.state.filters[this.state.selectedTab]}
                                updateFilter={this.updateFilter}
                                allDcsDuringRecover={this.state.allDcsDuringRecover}
                                isEditor={this.state.isEditor}
                                towerSync={this.state.TowerSync}
                                dcWaitingForRecover={this.state.dcWaitingForRecover}
                                showErrorDot={this.state.showErrorDot}
                                showPushServicesErrorDot={this.state.showPushServicesErrorDot}
                                showNodesWarningDot={this.state.showNodesWarningDot}
                                isAryaEnabled={this.state.isAryaEnabled}
                                isMfaOrPasEnabledByLicense={this.props.isMfaOrPasEnabledByLicense}
                                isCloudOnly={this.props.isCloudOnly}
                            />
                        </div>
                        {this.state.confirmationMessage.showConfirmation && (
                            <ConfirmationMessage
                                title={this.state.confirmationMessage.title}
                                body={this.state.confirmationMessage.body}
                                approveText={this.state.confirmationMessage.approveText}
                                checkboxText={this.state.confirmationMessage.checkboxText}
                                checkboxExtraData={this.state.confirmationMessage.checkboxExtraData}
                                checkBoxInitialState={this.state.confirmationMessage.checkBoxInitialState}
                                OnCancel={this.state.confirmationMessage.onCancel}
                                OnApprove={this.state.confirmationMessage.onApprove}
                                approveStyle={this.state.confirmationMessage.approveStyle}
                            />
                        )}
                        {this.state.showImportBackupModal && (
                            <ImportBackup
                                onClose={() => this.setState({ showImportBackupModal: false })}
                                onImport={() => {
                                    window.location.reload();
                                }}
                            />
                        )}
                        <CreateBackup
                            onClose={() => this.setState({ showCreateBackupModal: false })}
                            show={this.state.showCreateBackupModal}
                            onCreate={this.onBackupCreation}
                            towerVersion={this.state.TowerVersion}
                            backupPasswordInfo={backupPasswordInfo}
                            getBackupPasswordError={getBackupPasswordError}
                        />
                        <SchedulerBackup
                            onClose={() => this.setState({ showSchedulersBackup: false })}
                            onCreate={this.onScheduleBackupCreation}
                            show={this.state.showSchedulersBackup}
                            scheduleBackupRollingBack={this.state.scheduleBackupRollingBack}
                            backupPasswordInfo={backupPasswordInfo}
                            getBackupPasswordError={getBackupPasswordError}
                        />
                        <Snackbar
                            open={
                                this.state.logsToSend.length &&
                                this.state.logsActions[this.state.logsActions.length - 1] === LOGS_ACTIONS.SENDING
                            }>
                            <div className="bg-sf-gray-800 p-3 rounded-lg flex">
                                <img className="animate-spin" src={loadingIcon} alt="" />
                                <div className="pl-2 text-white text-sf">
                                    Sending logs...
                                    {`(${
                                        this.state.logsToSend.filter(
                                            (log) =>
                                                !this.state.logsToSendInProgress.find(
                                                    (logInProgress) => log === logInProgress
                                                )
                                        ).length
                                    }/${this.state.logsToSend.length})`}
                                </div>
                            </div>
                        </Snackbar>
                        <Snackbar
                            open={
                                this.state.logsActions[this.state.logsActions.length - 1] === LOGS_ACTIONS.SENDING_ENDED
                            }>
                            <div className="bg-sf-gray-800 p-3 rounded-lg flex">
                                <img src={vCircleIcon} alt="" />
                                <div className="pl-2 text-white text-sf">Logs sent successfully!</div>
                            </div>
                        </Snackbar>
                        <Snackbar
                            open={
                                this.state.logsToGenerate.length &&
                                this.state.logsActions[this.state.logsActions.length - 1] === LOGS_ACTIONS.GENERATING
                            }>
                            <div className="bg-sf-gray-800 p-3 rounded-lg flex">
                                <img className="animate-spin" src={loadingIcon} alt="" />
                                <div className="pl-2 text-white text-sf">
                                    Generating logs...
                                    {`(${
                                        this.state.logsToGenerate.filter(
                                            (log) =>
                                                !this.state.logsToGenerateInProgress.find(
                                                    (logInProgress) => log === logInProgress
                                                )
                                        ).length
                                    }/${this.state.logsToGenerate.length})`}
                                </div>
                            </div>
                        </Snackbar>
                        <Snackbar
                            open={
                                this.state.logsActions[this.state.logsActions.length - 1] ===
                                LOGS_ACTIONS.GENERATING_ENDED
                            }>
                            <div className="bg-sf-gray-800 p-3 rounded-lg flex">
                                <div className="pr-2 text-white text-sf">Logs generated successfully!</div>
                                <img src={vCircleIcon} alt="" />
                            </div>
                        </Snackbar>
                        <SnackbarSelectActions
                            open={
                                this.state.selectedTab === SYSTEM_STATUS_TABS.NODES &&
                                this.state.selectedNodes.size &&
                                this.state.logsActions[this.state.logsActions.length - 1] === LOGS_ACTIONS.SELECTING
                            }
                            name="Nodes"
                            nameSingleSelected="Node"
                            selectedSum={this.state.selectedNodes.size}
                            buttons={
                                !isOnPrem
                                    ? [{ text: 'Send Logs', onClick: this.sendSelectedNodesLogs }]
                                    : [
                                          { text: 'Generate Logs', onClick: this.generateSelectedNodesLogs },
                                          { text: 'Send Logs', onClick: this.sendSelectedNodesLogs }
                                      ]
                            }
                            onClickExit={() => this.setState({ selectedNodes: new Set() }, this.updateNodeRows)}
                        />
                        <SnackbarSelectActions
                            open={
                                this.state.selectedTab === SYSTEM_STATUS_TABS.DCS &&
                                this.state.selectedDcs.size &&
                                this.state.logsActions[this.state.logsActions.length - 1] === LOGS_ACTIONS.SELECTING
                            }
                            name="Domain Controller"
                            nameSingleSelected="Domain Controllers"
                            selectedSum={this.state.selectedDcs.size}
                            buttons={
                                !isOnPrem
                                    ? [{ text: 'Send Logs', onClick: this.sendSelectedDcsLogs }]
                                    : [
                                          { text: 'Generate Logs', onClick: this.generateSelectedDcsLogs },
                                          { text: 'Send Logs', onClick: this.sendSelectedDcsLogs }
                                      ]
                            }
                            onClickExit={() => this.setState({ selectedDcs: new Set() }, this.updateDcsRows)}
                        />
                        <SnackbarSelectActions
                            open={
                                this.state.selectedTab === SYSTEM_STATUS_TABS.PUSH_SERVICES &&
                                this.state.selectedPushServices.size &&
                                this.state.logsActions[this.state.logsActions.length - 1] === LOGS_ACTIONS.SELECTING
                            }
                            name="Desktop Messaging Services"
                            nameSingleSelected="Desktop Messaging Service"
                            selectedSum={this.state.selectedPushServices.size}
                            buttons={
                                !isOnPrem
                                    ? [{ text: 'Send Logs', onClick: this.sendSelectedPushServicesLogs }]
                                    : [
                                          { text: 'Generate Logs', onClick: this.generateSelectedPushServicesLogs },
                                          { text: 'Send Logs', onClick: this.sendSelectedPushServicesLogs }
                                      ]
                            }
                            onClickExit={() =>
                                this.setState({ selectedPushServices: new Set() }, this.updatePushServicesRows)
                            }
                        />
                        <SnackbarSelectActions
                            open={
                                this.state.selectedTab === SYSTEM_STATUS_TABS.AD_CONNECT &&
                                this.state.selectedAdConnects.size &&
                                this.state.logsActions[this.state.logsActions.length - 1] === LOGS_ACTIONS.SELECTING
                            }
                            name="AD Connects"
                            nameSingleSelected="AD Connect"
                            selectedSum={this.state.selectedAdConnects.size}
                            buttons={[{ text: 'Send Logs', onClick: this.sendSelectedAdConnectsLogs }]}
                            onClickExit={() =>
                                this.setState({ selectedAdConnects: new Set() }, this.updateAdConnectRows)
                            }
                        />
                        <SnackbarSelectActions
                            open={
                                this.state.selectedTab === SYSTEM_STATUS_TABS.ADMIN_CONSOLE &&
                                this.state.isAdminConsoleSelected &&
                                this.state.logsActions[this.state.logsActions.length - 1] === LOGS_ACTIONS.SELECTING
                            }
                            name="Admin Console"
                            nameSingleSelected="Admin Console"
                            selectedSum={1}
                            buttons={
                                !isOnPrem
                                    ? [{ text: 'Send Logs', onClick: this.SendTowerLog }]
                                    : [
                                          { text: 'Generate Logs', onClick: this.generateAdminConsoleLogs },
                                          { text: 'Send Logs', onClick: this.SendTowerLog }
                                      ]
                            }
                            onClickExit={() =>
                                this.setState({ isAdminConsoleSelected: false }, this.updateAdminConsoleRows)
                            }
                        />

                        <Snackbar open={this.state.creatingBackup}>
                            <div className="bg-sf-gray-800 p-3 rounded-lg flex">
                                <img className="animate-spin" src={loadingIcon} alt="" />
                                <div className="pl-2 text-white text-sf">Backing up system</div>
                            </div>
                        </Snackbar>
                        <Snackbar open={this.state.finishCreatingBackup}>
                            <div className="bg-sf-gray-800 p-3 rounded-lg flex">
                                <img src={vCircleIcon} alt="" />
                                <div className="pl-2 text-white text-sf">System Backup file created successfully</div>
                            </div>
                        </Snackbar>
                        <Snackbar open={this.state.showScheduleBackupRollBack}>
                            <div className="bg-sf-gray-800 p-3 rounded-lg flex">
                                <div className="pl-2 text-white text-sf">{this.state.schedulerDescription}</div>
                                <div
                                    className="pl-3 text-white text-sf border-l-sf-gray-700 cursor-pointer"
                                    onClick={this.rollBackScheduler}>
                                    Undo
                                </div>
                            </div>
                        </Snackbar>
                    </div>
                </div>
            </div>
        );
    }
}

const SystemStatusWithMode = () => {
    const { pricingModulesState } = usePricingModulesState();
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const forceBackupForSaasSupport = useFeature(FEATURE_FLAGS_NAMES.ForceBackupForSaasSupport);
    const isCloudOnly = useIsCloudOnly();
    const systemBackupEnabled = useFeature(FEATURE_FLAGS_NAMES.SystemBackupEnabled);
    const isMfaOrPasEnabledByLicense =
        pricingModulesState &&
        isAnyEnablingModulesActive(
            {
                [PricingModules.MFA_USERS]: RequiredModuleState.ON_PREM_ENABLED,
                [PricingModules.PRIVILEGED_ACCESS_SECURITY]: RequiredModuleState.ON_PREM_ENABLED
            },
            pricingModulesState
        );

    const isPoliciesAndAdDataColumnsEnabledByLicense =
        pricingModulesState &&
        isAnyEnablingModulesActive(
            {
                [PricingModules.MFA_USERS]: RequiredModuleState.ON_PREM_ENABLED,
                [PricingModules.AUTHENTICATION_FIREWALL]: RequiredModuleState.ON_PREM_ENABLED,
                [PricingModules.PRIVILEGED_ACCESS_SECURITY]: RequiredModuleState.ON_PREM_ENABLED,
                [PricingModules.NHI_PROTECTION]: RequiredModuleState.ON_PREM_ENABLED
            },
            pricingModulesState
        );

    if (!systemMode) {
        return <></>;
    }
    return (
        <SystemStatus
            systemMode={systemMode}
            isCloudOnly={isCloudOnly}
            forceBackupForSaasSupport={isFeatureActive(forceBackupForSaasSupport)}
            systemBackupEnabled={isFeatureActive(systemBackupEnabled)}
            pasFeatureEnabled={!!pricingModulesState?.[PricingModules.PRIVILEGED_ACCESS_SECURITY].onPremEnabled}
            isMfaOrPasEnabledByLicense={isMfaOrPasEnabledByLicense}
            isPoliciesAndAdDataColumnsEnabledByLicense={isPoliciesAndAdDataColumnsEnabledByLicense}
        />
    );
};

export default SystemStatusWithMode;
